import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import {
  canUseControls,
  useControlDevices,
  useBridgeStatus,
  useAquaBridgeStatus,
  useHaBridgeStatus,
  useControlPrograms,
  useControlLayout,
  useControlFavorites,
  saveControlFavorites,
  useAutoOffSnooze,
  saveAutoOffSnooze,
  useLiveConfig,
  sendControlCommand,
  sendProgramCommand,
  sendAquaCommand,
  sendDjCommand,
  sendHaCommand,
  watchDjViz,
  watchCommand,
  awaitCommandResult,
  useMediaFeed,
  useShadeBridgeStatus,
  sendShadeCommand,
  useNetworkDevices,
  useRingBridgeStatus,
  useCameraAlerts,
  saveCameraAlerts,
} from '../data/firestore';
import HouseMap from '../components/HouseMap';
import NetworkDiagram from '../components/NetworkDiagram';

// Smart-home controls: live switch/scene state mirrored from the ISY-994i by
// the Python bridge (bridge/isy_bridge.py). We only ever CREATE command docs;
// the bridge executes them and the device docs update via its websocket
// stream — so state here reflects reality, including wall-switch presses.
// Three tabs: Map (floorplan with live dots), Devices (cards), Programs
// (ISY automations, decoded to plain English by the bridge).

const BRIDGE_STALE_MS = 90 * 1000;

const pct = (level) => Math.round(((level || 0) / 255) * 100);

// Home (the quick-access favorites) is the default tab — on the phone
// home-screen app this page IS the house remote. Every device class then
// gets its own tab; the bar scrolls horizontally on phones.
const TABS = [
  { id: 'devices', label: 'Home' },
  { id: 'lights', label: 'Lights' },
  { id: 'shades', label: 'Shades' },
  { id: 'climate', label: 'Climate' },
  { id: 'pool', label: 'Pool & spa' },
  { id: 'media', label: 'Media' },
  { id: 'cameras', label: 'Cameras' },
  { id: 'map', label: 'Map' },
  { id: 'programs', label: 'Programs' },
  { id: 'network', label: 'Network' },
];

// Route a device command to the right backend: iAquaLink devices ride the
// same queue with target: 'aqua', Bluetooth lights (Home Assistant on the
// house box) with target: 'ha'; everything else goes to the ISY.
// "51m ago" / "3h ago" label for last-known water temps (sensors read blank
// when the pump is off; the bridge carries the last real reading forward).
const agoLabel = (ms) => {
  const min = Math.max(1, Math.round((Date.now() - ms) / 60000));
  if (min < 60) return `${min}m ago`;
  if (min < 48 * 60) return `${Math.round(min / 60)}h ago`;
  return `${Math.round(min / (24 * 60))}d ago`;
};

const sendDeviceCommand = (device, cmd, { level, value, rgb, kelvin, position, volume, app, url, button, query, videoId, code, temp, mode, fan, preset, swing, pct, minutes, email } = {}) => {
  if (device.vendor === 'aqualink') return sendAquaCommand(device.address, cmd, { value, email });
  if (device.vendor === 'ha') return sendHaCommand(device.address, cmd, { level, value, rgb, kelvin, volume, app, url, button, query, videoId, code, temp, mode, fan, preset, swing, pct, minutes, email });
  if (device.vendor === 'shade') return sendShadeCommand(device.address, cmd, { position, email });
  return sendControlCommand(device.address, cmd, { level, email });
};

function DeviceCard({ device, email, pending, onPending, fav, onFav }) {
  const busy = !!pending;
  const [draftPct, setDraftPct] = useState(null);
  // Optimistic target state: the toggle flips the instant you tap; the real
  // state confirms (or corrects) it when the bridge writes the device doc.
  const [optimistic, setOptimistic] = useState(null);
  const sliderTimer = useRef(null);
  const sentAt = useRef(null);

  // Confirmation: the bridge stamps every device write with updatedAt (epoch
  // ms). Any update after our command means fresh state — clear the spinner
  // right then instead of waiting out a fixed timeout. 3s of clock-skew slack.
  // Optimistic daylight-tracking state (Dyson lamps), same lifecycle as the
  // main toggle's optimistic value.
  const [optimisticDay, setOptimisticDay] = useState(null);

  useEffect(() => {
    if (busy && sentAt.current && (device.updatedAt || 0) >= sentAt.current - 3000) {
      onPending(device.address, false);
      sentAt.current = null;
      setOptimistic(null);
      setOptimisticDay(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device.updatedAt, busy]);

  // If the fallback timeout cleared pending (bridge down), drop the
  // optimistic state too so the card falls back to reality.
  useEffect(() => {
    if (!busy) {
      setOptimistic(null);
      setOptimisticDay(null);
    }
  }, [busy]);

  const [draftRgb, setDraftRgb] = useState(null);
  const [draftCt, setDraftCt] = useState(null);
  const colorTimer = useRef(null);
  const ctTimer = useRef(null);

  const send = async (cmd, opts = {}) => {
    // Preview (demo) devices: no backend behind them — the optimistic state
    // simply sticks, so the tab is explorable before iAquaLink is connected.
    if (device.demo) return;
    sentAt.current = Date.now();
    onPending(device.address, true);
    try {
      await sendDeviceCommand(device, cmd, { ...opts, email });
    } catch (err) {
      console.error('command failed', err);
      onPending(device.address, false);
      setOptimistic(null);
    }
  };

  // Debounce slider drags so we don't flood the queue while scrubbing.
  const onSlide = (value) => {
    setDraftPct(value);
    setOptimistic(value > 0);
    clearTimeout(sliderTimer.current);
    sliderTimer.current = setTimeout(() => {
      setDraftPct(null);
      send('level', { level: Math.round((value / 100) * 255) });
    }, 350);
  };

  // Bluetooth lamps (vendor 'ha'): debounced RGB picker + white-temp slider.
  const onColor = (hex) => {
    setDraftRgb(hex);
    clearTimeout(colorTimer.current);
    colorTimer.current = setTimeout(() => {
      setDraftRgb(null);
      send('color', { rgb: hex });
    }, 350);
  };
  const onCt = (kelvin) => {
    setDraftCt(kelvin);
    clearTimeout(ctTimer.current);
    ctTimer.current = setTimeout(() => {
      setDraftCt(null);
      send('ct', { kelvin });
    }, 350);
  };

  const shownOn = optimistic != null ? optimistic : device.isOn;
  const shownPct = draftPct != null ? draftPct : pct(device.level);
  const shownRgb = draftRgb != null ? draftRgb : device.colorRgb || '#ffffff';
  const shownCt = draftCt != null ? draftCt : device.colorTempK;
  const hasCt = !!(device.minColorTempK && device.maxColorTempK);
  // Dyson lamps: daylightMode is true/false/null (null = state unknown, the
  // feature still exists); absent entirely on other lights.
  const hasDaylight = device.daylightMode !== undefined;
  const shownDay = optimisticDay != null ? optimisticDay : device.daylightMode;
  const isScene = device.kind === 'scene';

  // Compact single-row layout: toggle + name (+ scene buttons) share the
  // head line; only dimmables get a second row for the slider.
  return (
    <div className={`ah-ctl-card ${shownOn ? 'on' : ''}`}>
      <div className="ah-ctl-head">
        {!isScene && (
          <button
            type="button"
            className={`ah-ctl-toggle ${shownOn ? 'on' : ''}`}
            aria-label={shownOn ? 'Turn off' : 'Turn on'}
            onClick={() => {
              const target = !shownOn;
              setOptimistic(target);
              send(target ? 'on' : 'off');
            }}
          >
            <span className="ah-ctl-knob" />
          </button>
        )}
        <span className="ah-ctl-name" title={device.address}>{device.name}</span>
        {!device.reachable && !isScene && <span className="ah-ctl-flag">no status</span>}
        {!isScene && device.motion && <span className="ah-ctl-flag ah-ctl-motion">motion</span>}
        {isScene && (
          <span className="ah-ctl-scene-btns">
            <button className="cs-btn cs-btn-sm" disabled={busy} onClick={() => { setOptimistic(true); send('on'); }}>
              Activate
            </button>
            <button className="cs-btn cs-btn-outline cs-btn-sm" disabled={busy} onClick={() => { setOptimistic(false); send('off'); }}>
              Off
            </button>
            {/* Insteon BRT/DIM steps — every responder nudges from its own
                current level (scenes have no single brightness to set). */}
            <button className="cs-btn cs-btn-outline cs-btn-sm" title="Dim" aria-label="Dim scene" onClick={() => send('dim')}>
              −
            </button>
            <button className="cs-btn cs-btn-outline cs-btn-sm" title="Brighten" aria-label="Brighten scene" onClick={() => send('brt')}>
              +
            </button>
          </span>
        )}
        {busy && <span className="ah-ctl-spinner" aria-label="working" />}
        {onFav && (
          <button
            type="button"
            className={`ah-ctl-star ${fav ? 'active' : ''}`}
            aria-label={fav ? 'Remove from favorites' : 'Add to favorites'}
            title={fav ? 'Remove from favorites' : 'Add to favorites'}
            onClick={onFav}
          >
            {fav ? '★' : '☆'}
          </button>
        )}
      </div>
      {!isScene && device.dimmable && (
        <div className="ah-ctl-row">
          <div className="ah-ctl-dim">
            <input
              type="range"
              min="0"
              max="100"
              value={shownPct}
              onChange={(e) => onSlide(Number(e.target.value))}
            />
            <span className="ah-ctl-pct">{shownPct}%</span>
          </div>
        </div>
      )}
      {!isScene && (device.colorCapable || hasCt) && (
        <div className="ah-ctl-row ah-ctl-color-row">
          {device.colorCapable && (
            <label className="ah-ctl-color" title="Color">
              <input type="color" value={shownRgb} onChange={(e) => onColor(e.target.value)} />
            </label>
          )}
          {hasCt && (
            <div className="ah-ctl-dim ah-ctl-ct" title="White temperature">
              <input
                type="range"
                min={device.minColorTempK}
                max={device.maxColorTempK}
                step="50"
                value={shownCt || device.minColorTempK}
                onChange={(e) => onCt(Number(e.target.value))}
              />
              <span className="ah-ctl-pct">{shownCt ? `${shownCt}K` : '—'}</span>
            </div>
          )}
        </div>
      )}
      {!isScene && hasDaylight && (
        <div className="ah-ctl-row">
          <button
            type="button"
            className={`cs-btn cs-btn-sm ${shownDay ? '' : 'cs-btn-outline'}`}
            disabled={busy}
            title="Automatically tracks the sun — the lamp adjusts brightness and color temperature to local daylight"
            onClick={() => {
              const target = !shownDay;
              setOptimisticDay(target);
              send('daylight', { value: target ? 'on' : 'off' });
            }}
          >
            {`Daylight tracking ${shownDay ? 'on' : shownDay === false ? 'off' : '—'}`}
          </button>
        </div>
      )}
    </div>
  );
}

const timeLabel = (ms) =>
  new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

// Dense quick-access favorites (UD Mobile style): explicit on/off toggle +
// name up top, − state + footer. Dimmers step ±2% per tap (stacking);
// Insteon scenes have no single brightness, so −/+ send group BRT/DIM
// steps and each responder moves from its own current level. Devices under
// a scheduled auto-off sweep (deck heaters, spa) also get the shared
// snooze bar — one tap pauses ALL sweeps for 5h (it's one house-wide
// snooze, so it shows active on every swept tile).
function FavTile({ device, email, pending, onPending, sweep, snoozedUntil, onSnooze }) {
  const busy = !!pending;
  const [optimistic, setOptimistic] = useState(null);
  const sentAt = useRef(null);

  useEffect(() => {
    if (busy && sentAt.current && (device.updatedAt || 0) >= sentAt.current - 3000) {
      onPending(device.address, false);
      sentAt.current = null;
      setOptimistic(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device.updatedAt, busy]);
  useEffect(() => {
    if (!busy) setOptimistic(null);
  }, [busy]);

  const send = (cmd, opts = {}) => {
    if (device.demo) return;
    sentAt.current = Date.now();
    onPending(device.address, true);
    sendDeviceCommand(device, cmd, { ...opts, email }).catch((err) => {
      console.error('command failed', err);
      onPending(device.address, false);
      setOptimistic(null);
    });
  };

  const isScene = device.kind === 'scene';
  const shownOn = optimistic != null ? optimistic : device.isOn;
  const adjustable = isScene || device.dimmable;
  // Rapid taps must stack on the last COMMANDED percent, not the reported
  // level (which lags the bridge round-trip) — otherwise every tap recomputes
  // from the same stale base. Reset once the device reports a new level.
  const nudgeBase = useRef(null);
  useEffect(() => {
    nudgeBase.current = null;
  }, [device.level]);
  const nudge = (dir) => {
    if (isScene) {
      send(dir > 0 ? 'brt' : 'dim');
      return;
    }
    const base = nudgeBase.current != null ? nudgeBase.current : pct(device.level);
    const next = Math.max(0, Math.min(100, base + dir * 2));
    nudgeBase.current = next;
    setOptimistic(next > 0);
    send('level', { level: Math.round((next / 100) * 255) });
  };

  const stateLabel = busy ? '…' : shownOn ? (device.dimmable ? `${pct(device.level)}%` : 'On') : 'Off';

  return (
    <div className={`ah-fav-tile ${shownOn ? 'on' : ''}`}>
      <div className="ah-fav-head">
        <button
          type="button"
          className={`ah-ctl-toggle ah-fav-toggle ${shownOn ? 'on' : ''}`}
          aria-label={shownOn ? `Turn ${device.name} off` : `Turn ${device.name} on`}
          onClick={() => {
            const target = !shownOn;
            setOptimistic(target);
            send(target ? 'on' : 'off');
          }}
        >
          <span className="ah-ctl-knob" />
        </button>
        <span className="ah-fav-name" title={device.address}>{device.name}</span>
      </div>
      <div className="ah-fav-adjust">
        {adjustable ? (
          <>
            <button type="button" aria-label="Dim" onClick={() => nudge(-1)}>−</button>
            <span className="ah-fav-state">{stateLabel}</span>
            <button type="button" aria-label="Brighten" onClick={() => nudge(1)}>+</button>
          </>
        ) : (
          <span className="ah-fav-state">{stateLabel}</span>
        )}
      </div>
      {sweep && (
        <button
          type="button"
          className={`ah-fav-snooze ${snoozedUntil ? 'active' : ''}`}
          title="Pause all scheduled auto-off sweeps (heaters + spa) for 5 hours"
          onClick={onSnooze}
        >
          {snoozedUntil ? `Zz until ${timeLabel(snoozedUntil)}` : 'Zz snooze 5h'}
        </button>
      )}
    </div>
  );
}

// Motorized shade (Rollease Pulse 2 via bridge/shade_bridge.py). The bridge
// stores position as percent CLOSED; the card displays and edits percent
// OPEN (what people mean by "open the shade halfway"). Battery shades sleep
// between commands — 'asleep' is normal and moves still land (the hub wakes
// them); the bridge live-tracks real positions during motion.
function ShadeCard({ device, email, pending, onPending }) {
  const busy = !!pending;
  const [draftOpen, setDraftOpen] = useState(null);
  const sliderTimer = useRef(null);
  const sentAt = useRef(null);

  useEffect(() => {
    if (busy && sentAt.current && (device.updatedAt || 0) >= sentAt.current - 3000) {
      onPending(device.address, false);
      sentAt.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device.updatedAt, busy]);

  const send = async (cmd, opts = {}) => {
    sentAt.current = Date.now();
    onPending(device.address, true);
    try {
      await sendDeviceCommand(device, cmd, { ...opts, email });
    } catch (err) {
      console.error('shade command failed', err);
      onPending(device.address, false);
    }
  };

  const openPct = device.position == null ? null : 100 - device.position;
  const shownOpen = draftOpen ?? openPct;
  const stateLabel =
    device.moving ? 'moving…'
      : openPct == null ? '—'
        : openPct <= 0 ? 'Closed'
          : openPct >= 100 ? 'Open'
            : `${openPct}% open`;

  const onSlider = (val) => {
    setDraftOpen(val);
    clearTimeout(sliderTimer.current);
    sliderTimer.current = setTimeout(() => {
      setDraftOpen(null);
      send('position', { position: 100 - val });
    }, 500);
  };

  return (
    <div className={`ah-ctl-card ${busy ? 'busy' : ''}`}>
      <div className="ah-ctl-head">
        <span className="ah-ctl-name" title={device.address}>{device.name}</span>
        <span className="ah-shade-state">{stateLabel}</span>
        <span className="ah-ctl-scene-btns">
          <button type="button" className="cs-btn cs-btn-sm" disabled={busy} onClick={() => send('open')}>
            Open
          </button>
          <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => send('stop')}>
            Stop
          </button>
          <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" disabled={busy} onClick={() => send('close')}>
            Close
          </button>
        </span>
        {busy && <span className="ah-ctl-spinner" aria-label="working" />}
      </div>
      <div className="ah-ctl-row">
        <div className="ah-ctl-dim" title="Percent open">
          <input
            type="range"
            min="0"
            max="100"
            step="5"
            value={shownOpen ?? 0}
            onChange={(e) => onSlider(Number(e.target.value))}
          />
          <span className="ah-ctl-pct">{shownOpen == null ? '—' : `${shownOpen}%`}</span>
        </div>
      </div>
    </div>
  );
}

// AC / thermostats (kind 'climate', Gree via the HA bridge). Mode chips +
// a debounced setpoint stepper; HA state string IS the hvac mode.
const HVAC_LABELS = { auto: 'Auto', cool: 'Cool', dry: 'Dry', fan_only: 'Fan', heat: 'Heat' };

// "fixed_upper_middle" -> "fixed upper middle" for the swing dropdowns.
const swingLabel = (s) => s.replace(/_/g, ' ').replace('full swing', 'full swing (auto)');

function ClimateCard({ device, email, pending, onPending }) {
  const busy = !!pending;
  const [optimisticMode, setOptimisticMode] = useState(null);
  const [draftTemp, setDraftTemp] = useState(null);
  const tempTimer = useRef(null);
  const sentAt = useRef(null);

  useEffect(() => {
    if (busy && sentAt.current && (device.updatedAt || 0) >= sentAt.current - 3000) {
      onPending(device.address, false);
      sentAt.current = null;
      setOptimisticMode(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device.updatedAt, busy]);
  useEffect(() => {
    if (!busy) setOptimisticMode(null);
  }, [busy]);

  const send = async (cmd, opts = {}) => {
    sentAt.current = Date.now();
    onPending(device.address, true);
    try {
      await sendDeviceCommand(device, cmd, { ...opts, email });
    } catch (err) {
      console.error('climate command failed', err);
      onPending(device.address, false);
      setOptimisticMode(null);
    }
  };

  const mode = optimisticMode ?? device.state;
  const isOn = !!mode && mode !== 'off';
  const shownTemp = draftTemp ?? device.setpoint;
  const modes = (device.hvacModes || []).filter((m) => m !== 'off');

  const nudgeTemp = (dir) => {
    const base = draftTemp ?? device.setpoint ?? 72;
    const next = Math.max(device.minTemp || 46, Math.min(device.maxTemp || 86, base + dir));
    setDraftTemp(next);
    clearTimeout(tempTimer.current);
    tempTimer.current = setTimeout(() => {
      setDraftTemp(null);
      send('set_temp', { temp: next });
    }, 600);
  };

  return (
    <div className={`ah-ctl-card ${isOn ? 'on' : ''}`}>
      <div className="ah-ctl-head">
        <button
          type="button"
          className={`ah-ctl-toggle ${isOn ? 'on' : ''}`}
          aria-label={isOn ? 'Turn off' : 'Turn on'}
          onClick={() => {
            setOptimisticMode(isOn ? 'off' : 'cool');
            send(isOn ? 'off' : 'on');
          }}
        >
          <span className="ah-ctl-knob" />
        </button>
        <span className="ah-ctl-name" title={device.address}>{device.name}</span>
        {!device.reachable && <span className="ah-ctl-flag">no status</span>}
        {busy && <span className="ah-ctl-spinner" aria-label="working" />}
      </div>
      {/* Same "now → setpoint" row the pool thermostats use — the name owns
          the head line, the temps get their own. */}
      <div className="ah-aqua-temp-row">
        <span className="ah-aqua-now">{device.currentTemp != null ? `${device.currentTemp}°` : '—'}</span>
        <span className="ah-aqua-arrow">→</span>
        <div className="ah-aqua-set">
          <button
            type="button"
            className="cs-btn cs-btn-outline cs-btn-sm"
            aria-label="Cooler"
            disabled={!isOn}
            onClick={() => nudgeTemp(-1)}
          >
            −
          </button>
          <span className="ah-aqua-sp">{shownTemp != null ? `${shownTemp}°` : '—'}</span>
          <button
            type="button"
            className="cs-btn cs-btn-outline cs-btn-sm"
            aria-label="Warmer"
            disabled={!isOn}
            onClick={() => nudgeTemp(1)}
          >
            +
          </button>
        </div>
      </div>
      <div className="ah-ctl-row ah-clim-row">
        <span className="ah-ctl-scene-btns">
          {modes.map((m) => (
            <button
              key={m}
              type="button"
              className={`cs-btn cs-btn-sm ${mode === m ? '' : 'cs-btn-outline'}`}
              onClick={() => {
                setOptimisticMode(m);
                send('set_mode', { mode: m });
              }}
            >
              {HVAC_LABELS[m] || m}
            </button>
          ))}
        </span>
      </div>
      <div className="ah-ctl-row ah-clim-row">
        {(device.fanModes || []).length > 0 && (
          <select
            value={device.fanMode || 'auto'}
            onChange={(e) => send('set_fan', { fan: e.target.value })}
            aria-label="Fan speed"
            disabled={!isOn}
          >
            {device.fanModes.map((f) => (
              <option key={f} value={f}>{`fan: ${f}`}</option>
            ))}
          </select>
        )}
        {/* Turbo/Eco/Sleep are HA presets (turbo = 'boost') — one active at
            a time; tapping the active one drops back to normal. */}
        {(device.presetModes || []).length > 0 && (
          <span className="ah-ctl-scene-btns">
            {[['boost', 'Turbo'], ['eco', 'Eco'], ['sleep', 'Sleep']]
              .filter(([p]) => device.presetModes.includes(p))
              .map(([p, label]) => (
                <button
                  key={p}
                  type="button"
                  className={`cs-btn cs-btn-sm ${device.presetMode === p ? '' : 'cs-btn-outline'}`}
                  disabled={!isOn}
                  onClick={() => send('set_preset', { preset: device.presetMode === p ? 'none' : p })}
                >
                  {label}
                </button>
              ))}
          </span>
        )}
      </div>
      {((device.swingModes || []).length > 0 || (device.swingHModes || []).length > 0) && (
        <div className="ah-ctl-row ah-clim-row">
          {(device.swingModes || []).length > 0 && (
            <select
              value={device.swingMode || 'default'}
              onChange={(e) => send('set_swing', { swing: e.target.value })}
              aria-label="Vertical louver (up/down)"
              disabled={!isOn}
            >
              {device.swingModes.map((s) => (
                <option key={s} value={s}>{`↕ ${swingLabel(s)}`}</option>
              ))}
            </select>
          )}
          {(device.swingHModes || []).length > 0 && (
            <select
              value={device.swingHMode || 'default'}
              onChange={(e) => send('set_swing_h', { swing: e.target.value })}
              aria-label="Horizontal vanes (left/right)"
              disabled={!isOn}
            >
              {device.swingHModes.map((s) => (
                <option key={s} value={s}>{`↔ ${swingLabel(s)}`}</option>
              ))}
            </select>
          )}
        </div>
      )}
    </div>
  );
}

// Dyson purifier fan (via ha_bridge): power, speed slider, auto/manual,
// night mode, oscillation, sleep timer — plus the live air-quality reading
// and filter health the purifier measures anyway.
const AQI_TONE = (cat) => {
  const c = String(cat || '').toLowerCase();
  if (c === 'good') return 'ok';
  if (c === 'moderate' || c === 'fair') return 'warn';
  return 'bad';
};

function FanCard({ device, email, pending, onPending }) {
  const busy = !!pending;
  const [optimisticOn, setOptimisticOn] = useState(null);
  const [draftSpeed, setDraftSpeed] = useState(null);
  const speedTimer = useRef(null);
  const sentAt = useRef(null);

  useEffect(() => {
    if (busy && sentAt.current && (device.updatedAt || 0) >= sentAt.current - 3000) {
      onPending(device.address, false);
      sentAt.current = null;
      setOptimisticOn(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device.updatedAt, busy]);
  useEffect(() => {
    if (!busy) setOptimisticOn(null);
  }, [busy]);

  const send = async (cmd, opts = {}) => {
    sentAt.current = Date.now();
    onPending(device.address, true);
    try {
      await sendDeviceCommand(device, cmd, { ...opts, email });
    } catch (err) {
      console.error('fan command failed', err);
      onPending(device.address, false);
      setOptimisticOn(null);
    }
  };

  const isOn = optimisticOn ?? device.isOn;
  const speed = draftSpeed ?? device.speedPct ?? 0;
  const auto = device.presetMode === 'auto';

  const onSpeed = (pct) => {
    setDraftSpeed(pct);
    clearTimeout(speedTimer.current);
    speedTimer.current = setTimeout(() => {
      setDraftSpeed(null);
      send('fan_speed', { pct });
    }, 500);
  };

  // Oscillation renders as one select: Off + the hub's angle options
  // ('Custom' shows if active but isn't offered as a pick).
  const oscModes = (device.oscModes || []).filter((m) => m !== 'Custom');
  const oscValue = device.oscillating ? device.oscMode || oscModes[0] : 'off';

  return (
    <div className={`ah-ctl-card ${isOn ? 'on' : ''}`}>
      <div className="ah-ctl-head">
        <button
          type="button"
          className={`ah-ctl-toggle ${isOn ? 'on' : ''}`}
          aria-label={isOn ? 'Turn off' : 'Turn on'}
          onClick={() => {
            setOptimisticOn(!isOn);
            send(isOn ? 'off' : 'on');
          }}
        >
          <span className="ah-ctl-knob" />
        </button>
        <span className="ah-ctl-name" title={device.address}>{device.name}</span>
        {device.aqi != null && (
          <span className={`ah-ctl-flag ah-fan-aqi ${AQI_TONE(device.aqiCategory)}`}>
            AQI {device.aqi}
          </span>
        )}
        {!device.reachable && <span className="ah-ctl-flag">no status</span>}
        {busy && <span className="ah-ctl-spinner" aria-label="working" />}
      </div>
      <div className="ah-ctl-row">
        <div className="ah-ctl-dim">
          <input
            type="range"
            min="10"
            max="100"
            step="10"
            value={speed}
            disabled={!isOn}
            onChange={(e) => onSpeed(Number(e.target.value))}
          />
          <span className="ah-ctl-pct">{speed}%</span>
        </div>
      </div>
      <div className="ah-ctl-row ah-clim-row">
        <span className="ah-ctl-scene-btns">
          {/* Auto lets the purifier chase its own air-quality reading. */}
          <button
            type="button"
            className={`cs-btn cs-btn-sm ${auto ? '' : 'cs-btn-outline'}`}
            disabled={!isOn}
            onClick={() => send('set_preset', { preset: auto ? 'manual' : 'auto' })}
          >
            Auto
          </button>
          <button
            type="button"
            className={`cs-btn cs-btn-sm ${device.nightMode ? '' : 'cs-btn-outline'}`}
            disabled={!isOn}
            onClick={() => send('night_mode', { value: device.nightMode ? 'off' : 'on' })}
          >
            Night
          </button>
        </span>
        {oscModes.length > 0 && (
          <select
            value={oscValue}
            onChange={(e) => (e.target.value === 'off'
              ? send('oscillate', { value: 'off' })
              : send('osc_mode', { value: e.target.value }))}
            aria-label="Oscillation"
            disabled={!isOn}
          >
            <option value="off">no sweep</option>
            {oscModes.map((m) => (
              <option key={m} value={m}>{`sweep ${m}`}</option>
            ))}
            {oscValue === 'Custom' && <option value="Custom">sweep custom</option>}
          </select>
        )}
      </div>
      <div className="ah-ctl-row ah-clim-row">
        <select
          value={device.sleepTimerMin || 0}
          onChange={(e) => send('sleep_timer', { minutes: Number(e.target.value) })}
          aria-label="Sleep timer"
          disabled={!isOn}
        >
          {[[0, 'no timer'], [30, 'off in 30 min'], [60, 'off in 1 h'], [120, 'off in 2 h'], [240, 'off in 4 h'], [480, 'off in 8 h']]
            .map(([m, label]) => <option key={m} value={m}>{label}</option>)}
          {![0, 30, 60, 120, 240, 480].includes(device.sleepTimerMin || 0) && (
            <option value={device.sleepTimerMin}>{`off in ${device.sleepTimerMin} min`}</option>
          )}
        </select>
      </div>
      <div className="ah-fan-stats">
        {device.aqiCategory && <span>{`air ${device.aqiCategory.toLowerCase()}`}</span>}
        {device.humidity != null && <span>{`${device.humidity}% rh`}</span>}
        {device.tempF != null && <span>{`${device.tempF}°`}</span>}
        {device.filterLifePct != null && !device.filterReplace && (
          <span>{`filter ${device.filterLifePct}%`}</span>
        )}
        {device.filterReplace && <span className="ah-fan-filter-warn">replace filter</span>}
      </div>
    </div>
  );
}

// Typed search on the TV, three targets. YouTube rides the Lounge (cast)
// API — "Play top" casts the best match, "Pick…" fetches the top results so
// you tap the one you want (tvOS YouTube ignores URL deep-link paths).
// "All apps" types into tvOS's own cross-app Search screen (needs the
// one-time Companion keyboard pairing). Netflix deep-links into its search
// page (a one-time Open/Cancel popup may appear — press Open).
const TV_SEARCH_TARGETS = ['YouTube', 'All apps', 'Netflix'];

// tvOS app names carry non-breaking spaces — normalize before comparing.
const nbsp = (s) => String(s || '').replace(/\u00a0/g, ' ');

// A "TV" is a media player whose source list carries tvOS apps. Sonos also
// reports a source list (TV, Line-in), so length alone misclassifies
// speakers as TVs.
const isTvDevice = (d) => (d.sourceList || []).some((s) => /youtube|netflix|app store/i.test(nbsp(s)));

// Quick-launch chips on the hero TV card: the first 5 of these found among
// the installed apps, in this order of preference.
const TOP_TV_APPS = ['YouTube', 'Netflix', 'Prime Video', 'Disney+', 'F1 TV', 'Apple TV', 'Max', 'Hulu', 'Plex', 'Spotify'];

// Which mediaFeeds doc backs each chip's browse rail (see
// backend/api/mediaFeedsWorker.js). YouTube is the owner's subscriptions;
// the rest are TMDB top picks. Apps without a feed (F1 TV) show no rail.
const FEED_IDS = {
  youtube: 'youtube',
  netflix: 'netflix',
  'prime video': 'prime-video',
  'disney+': 'disney-plus',
  'apple tv': 'apple-tv-plus',
  hulu: 'hulu',
  max: 'max',
};
const feedIdFor = (app) => FEED_IDS[nbsp(app).toLowerCase()] || null;

// Catmull-Rom: ease between the deck's discrete bins so a 30–120 Hz pack
// still reads as a continuous wave. We do not invent samples — we interpolate
// theirs. That's the "closest sync version" of the deck's pack.
function vizLerp(p0, p1, p2, p3, t) {
  const t2 = t * t;
  const t3 = t2 * t;
  return 0.5 * (
    (2 * p1)
    + (-p0 + p2) * t
    + (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2
    + (-p0 + 3 * p1 - 3 * p2 + p3) * t3
  );
}

function vizAt(samples, i, key) {
  const s = samples[Math.max(0, Math.min(samples.length - 1, i))];
  return s[key] ?? 0;
}

// Scrolling stereo waveform: the DJ deck owns the pack (docs/dj-stream.md).
// This card only DRAWS it. Right edge is encoder-now (newest pack); a
// playhead sits `delayMs` back — best-guess audible now (Icecast + Sonos
// buffer, live-config `djVizDelayMs`, default 4 s). Left of the line has
// already played; right of it is what's coming. Dial the delay when home.
// Mounted only while playback is verified. Deck packets win over Pi ffmpeg.
function DjWave({ delayMs, active }) {
  const canvasRef = useRef(null);
  const samplesRef = useRef([]); // [{t, l, r, lo?, md?, hi?}] per bin
  const sourceRef = useRef(null); // 'deck' once seen — never mix with 'pi'
  const skewRef = useRef(null);
  const delayRef = useRef(delayMs);
  delayRef.current = delayMs;

  useEffect(() => {
    if (!active) {
      samplesRef.current = [];
      sourceRef.current = null;
      skewRef.current = null;
      return undefined;
    }
    const unsub = watchDjViz((p) => {
      if (!Array.isArray(p.l) || !p.l.length || !p.t) return;
      const src = p.source === 'deck' ? 'deck' : 'pi';
      // Once the deck is publishing, ignore the Pi fallback — mixing two
      // clocks/shapes is worse than a short gap.
      if (sourceRef.current === 'deck' && src !== 'deck') return;
      if (src === 'deck' && sourceRef.current !== 'deck') {
        samplesRef.current = [];
        sourceRef.current = 'deck';
      } else if (!sourceRef.current) {
        sourceRef.current = src;
      }
      const n = p.l.length;
      const step = 1000 / (p.rate || n);
      const arr = samplesRef.current;
      const lastT = arr.length ? arr[arr.length - 1].t : 0;
      if (p.t <= lastT) return;
      for (let i = 0; i < n; i += 1) {
        const t = p.t - (n - 1 - i) * step;
        if (t > lastT) {
          arr.push({
            t,
            l: p.l[i] || 0,
            r: p.r?.[i] ?? p.l[i] ?? 0,
            lo: p.lo?.[i],
            md: p.md?.[i],
            hi: p.hi?.[i],
          });
        }
      }
      const skew = p.t - Date.now();
      skewRef.current = skewRef.current === null ? skew : skewRef.current * 0.7 + skew * 0.3;
      samplesRef.current = arr.filter((s) => s.t > p.t - 30000);
    });
    return unsub;
  }, [active]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    const BANDS_L = ['rgba(224, 148, 79, 0.95)', 'rgba(127, 216, 143, 0.95)', 'rgba(202, 236, 255, 0.95)'];
    const BANDS_R = ['rgba(224, 148, 79, 0.6)', 'rgba(127, 216, 143, 0.6)', 'rgba(202, 236, 255, 0.6)'];
    let raf;
    const draw = () => {
      const dpr = window.devicePixelRatio || 1;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (canvas.width !== Math.round(w * dpr)) {
        canvas.width = Math.round(w * dpr);
        canvas.height = Math.round(h * dpr);
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      const mid = h / 2;
      ctx.fillStyle = 'rgba(127, 216, 143, 0.22)';
      ctx.fillRect(0, mid - 0.5, w, 1);
      if (!active) {
        raf = requestAnimationFrame(draw);
        return;
      }
      // Encoder-now at the right edge. Playhead is delayMs earlier = the
      // best-guess audible sample (tunable via config/app.djVizDelayMs).
      const delay = delayRef.current || 4000;
      const PAST = 6000;
      const spanMs = PAST + delay;
      const end = Date.now() + (skewRef.current ?? 0);
      const start = end - spanMs;
      const nowT = end - delay;
      const samples = samplesRef.current;
      if (samples.length < 2) {
        raf = requestAnimationFrame(draw);
        return;
      }
      let idx = 0;
      for (let x = 0; x < w; x += 1) {
        const tx = start + (x / w) * spanMs;
        while (idx < samples.length - 1 && samples[idx + 1].t <= tx) idx += 1;
        const a = samples[idx];
        const b = samples[Math.min(idx + 1, samples.length - 1)];
        const gap = Math.max(40, (b.t - a.t) * 1.5 || 80);
        if (tx < a.t - gap || tx > b.t + gap) continue;
        const span = b.t - a.t;
        const f = span <= 0 ? 0 : Math.max(0, Math.min(1, (tx - a.t) / span));
        const samp = (key) => vizLerp(
          vizAt(samples, idx - 1, key),
          vizAt(samples, idx, key),
          vizAt(samples, idx + 1, key),
          vizAt(samples, idx + 2, key),
          f,
        );
        const up = (Math.max(0, samp('l')) / 100) * (mid - 2);
        const dn = (Math.max(0, samp('r')) / 100) * (mid - 2);
        if (a.lo != null && b.lo != null) {
          const parts = [samp('lo'), samp('md'), samp('hi')].map((v) => Math.max(0, v));
          const total = parts[0] + parts[1] + parts[2] || 1;
          let yU = mid;
          let yD = mid + 1;
          for (let k = 0; k < 3; k += 1) {
            const hU = (parts[k] / total) * up;
            const hD = (parts[k] / total) * dn;
            ctx.fillStyle = BANDS_L[k];
            ctx.fillRect(x, yU - hU, 1, hU);
            ctx.fillStyle = BANDS_R[k];
            ctx.fillRect(x, yD, 1, hD);
            yU -= hU;
            yD += hD;
          }
        } else {
          ctx.fillStyle = 'rgba(127, 216, 143, 0.9)';
          ctx.fillRect(x, mid - up, 1, up);
          ctx.fillStyle = 'rgba(118, 176, 232, 0.85)';
          ctx.fillRect(x, mid + 1, 1, dn);
        }
      }
      const playX = Math.max(0, Math.min(w, ((nowT - start) / spanMs) * w));
      ctx.fillStyle = 'rgba(0, 0, 0, 0.22)';
      ctx.fillRect(playX, 0, w - playX, h);
      ctx.fillStyle = 'rgba(255, 214, 102, 0.95)';
      ctx.fillRect(playX - 0.5, 0, 2, h);
      ctx.fillStyle = 'rgba(255, 214, 102, 0.95)';
      ctx.font = '600 10px ui-sans-serif, system-ui, sans-serif';
      ctx.textBaseline = 'top';
      const label = 'now';
      const lw = ctx.measureText(label).width;
      const lx = Math.min(w - lw - 4, Math.max(4, playX + 4));
      ctx.fillText(label, lx, 3);
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [active]);

  return <canvas ref={canvasRef} className="ah-dj-wave" />;
}

// House DJ panel: the AI DJ deck streams into Icecast on the house box
// (docs/dj-stream.md) and the Sonos zones tune in as one synced group.
// Zone chips ARE the desired group — tap to add/drop a room; dj_set sends
// the complete set each time, so the bridge reconciles grouping in one
// command. Volume rides the normal per-zone volume_set.
// `deck` is the relay's source status ({live, listeners, title} | null =
// unknown) mirrored by ha_bridge into _haBridge.djSource — it disambiguates
// "the deck isn't streaming" from "no rooms are tuned in".
// `mic` is _haBridge.djMic: the house USB mic's mount status plus roomDb /
// roomDbWin — per-second RMS dBFS actually heard in the room, refreshed
// with each 30 s heartbeat.
function DjPanel({ zones, email, deck, mic, vizDelayMs, hbFresh }) {
  // In-flight DJ command: {id, cmd, zones, startedAt}. The bridge narrates
  // stages onto the command doc (cmdDoc.stage) and Sonos buffers ~3-8 s
  // after "done", so the panel shows live progress + elapsed time instead
  // of a mute spinner — a silent gap made the owner re-press play.
  const [pending, setPending] = useState(null);
  const [cmdDoc, setCmdDoc] = useState(null);
  const [lastError, setLastError] = useState(null);
  const [, setTick] = useState(0); // re-render for the elapsed counter
  const [drafts, setDrafts] = useState({}); // address -> in-flight volume
  const volTimers = useRef({});

  const djZones = zones.filter((z) => z.djOn);
  const live = djZones.length > 0;
  // Verified house playback: a zone is djOn (playing + DJ content id).
  // Freshness is the HA-bridge heartbeat, NOT each zone's updatedAt —
  // a zone playing the same stream can go minutes without a state_changed
  // and must not unmount the wave.
  const playback = live && hbFresh !== false;
  const allOn = zones.length > 0 && zones.every((z) => z.djOn);
  const nowPlaying = djZones.find((z) => z.title)?.title;

  const djSig = djZones.map((z) => z.address).sort().join(',');

  // Follow the in-flight command doc + tick the elapsed counter.
  useEffect(() => {
    if (!pending?.id) return undefined;
    const unsub = watchCommand(pending.id, setCmdDoc);
    const timer = setInterval(() => setTick((n) => n + 1), 500);
    return () => {
      unsub();
      clearInterval(timer);
    };
  }, [pending?.id]);

  // Success = the MIRROR confirms the requested end state (djOn zones match
  // the request), not the command ack — Sonos can still refuse the stream
  // after a clean "done".
  useEffect(() => {
    if (!pending) return;
    const on = new Set(djZones.map((z) => z.address));
    const reached = pending.cmd === 'dj_off'
      ? on.size === 0
      : pending.zones.length > 0 && pending.zones.every((a) => on.has(a));
    if (reached) setPending(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [djSig, pending]);

  // Terminal command error: surface it and free the buttons.
  useEffect(() => {
    if (pending && cmdDoc?.id === pending.id && cmdDoc.status === 'error') {
      setLastError(cmdDoc.error || 'command failed');
      setPending(null);
    }
  }, [cmdDoc, pending]);

  const elapsedS = pending ? Math.floor((Date.now() - pending.startedAt) / 1000) : 0;
  const stalled = pending && elapsedS >= 30;
  // Hold the play affordances while a command works (re-pressing mid-group
  // just churns Sonos); Stop stays live per the never-lock-out rule.
  const inflight = Boolean(pending) && !stalled;

  const send = async (cmd, zoneAddrs) => {
    setLastError(null);
    setCmdDoc(null);
    try {
      const id = await sendDjCommand(cmd, { zones: zoneAddrs, email });
      setPending({ id, cmd, zones: zoneAddrs || [], startedAt: Date.now() });
    } catch (err) {
      console.error('dj command failed', err);
      setLastError('could not send the command — check your connection');
      setPending(null);
    }
  };

  const toggleZone = (z) => {
    const cur = djZones.map((x) => x.address);
    const next = z.djOn ? cur.filter((a) => a !== z.address) : [...cur, z.address];
    if (!next.length) return send('dj_off');
    return send('dj_set', next);
  };

  const nudgeVolume = (z, vol) => {
    setDrafts((d) => ({ ...d, [z.address]: vol }));
    clearTimeout(volTimers.current[z.address]);
    volTimers.current[z.address] = setTimeout(() => {
      setDrafts((d) => {
        const { [z.address]: _gone, ...rest } = d;
        return rest;
      });
      sendHaCommand(z.address, 'volume_set', { volume: vol, email }).catch((err) =>
        console.error('volume failed', err));
    }, 500);
  };

  return (
    <div className={`ah-ctl-card ah-dj-card ${live ? 'on' : ''}`}>
      <div className="ah-ctl-head">
        <span className="ah-ctl-name">House DJ</span>
        {live && <span className="ah-ctl-flag ah-dj-live">live</span>}
        {deck != null && (
          <span className={`ah-ctl-flag ah-dj-deck ${deck.live ? 'on' : 'off'}`}>
            {deck.live ? 'deck streaming' : 'deck offline'}
          </span>
        )}
        {pending && <span className="ah-ctl-spinner" aria-label="working" />}
      </div>
      <div className="ah-dj-now">
        {live
          ? (nowPlaying || deck?.title || 'Streaming — waiting for track info…')
          : deck?.live === false
            ? 'The DJ deck isn\u2019t streaming to the house relay — start the deck, then pick rooms.'
            : deck?.live
              ? `Deck is streaming${deck.title ? ` — ${deck.title}` : ''}. Pick rooms to tune them in.`
              : 'Pick rooms to tune them into the DJ deck\u2019s stream.'}
      </div>
      {/* Waveform only when house playback is verified. The canvas
          interpolates the deck's pack (Pi fallback until the deck publishes). */}
      {playback && <DjWave delayMs={vizDelayMs} active={playback} />}
      {/* Room loudness heard by the house USB mic (latest 1 s RMS, -60 dBFS
          noise floor → -20 loud). Ground truth that music is AUDIBLE, not
          just that Sonos claims to play. Sparkline = the last ~30 s. */}
      {hbFresh !== false && mic?.live && typeof mic.roomDb === 'number' && (
        <div className="ah-dj-room" title="Loudness heard by the house microphone (last 30s)">
          <span className="ah-dj-room-label">Room</span>
          <span className="ah-dj-room-meter">
            <span style={{ width: `${Math.max(0, Math.min(100, ((mic.roomDb + 60) / 40) * 100))}%` }} />
          </span>
          <span className="ah-dj-room-db">{Math.round(mic.roomDb)} dB</span>
          {Array.isArray(mic.roomDbWin) && mic.roomDbWin.length > 1 && (
            <svg className="ah-dj-room-spark" viewBox="0 0 60 14" preserveAspectRatio="none" aria-hidden="true">
              <polyline
                points={mic.roomDbWin.map((v, i) =>
                  `${(i / (mic.roomDbWin.length - 1)) * 60},${14 - Math.max(0, Math.min(1, (v + 60) / 40)) * 14}`).join(' ')}
                fill="none"
              />
            </svg>
          )}
        </div>
      )}
      {/* Live progress while a command runs: the bridge's stage narration,
          then the Sonos buffering window, with elapsed seconds — so the gap
          between pressing play and hearing music is visibly "working". */}
      {pending && (
        <div className={`ah-dj-progress ${stalled ? 'warn' : ''}`} aria-live="polite">
          {stalled
            ? 'Speakers still haven\u2019t confirmed after 30s — the deck stream may be down. Check the deck, then try again.'
            : cmdDoc?.status === 'done'
              ? (pending.cmd === 'dj_off'
                ? `Stopping speakers\u2026 ${elapsedS}s`
                : `Stream started \u2014 speakers buffering (usually \u2264 8s)\u2026 ${elapsedS}s`)
              : `${cmdDoc?.stage || 'Sending to the house bridge'}\u2026 ${elapsedS}s`}
        </div>
      )}
      {!pending && lastError && (
        <div className="ah-dj-progress err">Command failed: {lastError}</div>
      )}
      <div className="ah-ctl-row">
        <span className="ah-ctl-scene-btns">
          {/* Reads as an ACTION only while it would change something: with
              every room already on the DJ it flips to a quiet ✓ state
              (same pattern as Stop/Stopped). Still pressable — dj_set is
              the desired end state, so a resend just re-syncs the group. */}
          <button
            type="button"
            className={`cs-btn cs-btn-sm ${allOn ? 'cs-btn-outline ah-dj-allon' : ''}`}
            disabled={inflight}
            onClick={() => send('dj_set', zones.map((z) => z.address))}
          >
            <span className="ah-dj-glyph">{allOn ? '\u2713' : '\u25B6'}</span> Whole house
          </button>
          {/* Always pressable (a stale mirror must never lock the owner out
              of stopping the music), but the label reflects state: "Stopped"
              and muted when no zone is on the DJ. */}
          <button
            type="button"
            className={`cs-btn cs-btn-sm cs-btn-outline ${live ? '' : 'ah-dj-stopped'}`}
            onClick={() => send('dj_off')}
          >
            {live ? 'Stop' : 'Stopped'}
          </button>
        </span>
      </div>
      {zones.map((z) => (
        <div key={z.id} className="ah-dj-zone">
          {/* Explicit per-speaker play/stop — the room name is a label, the
              transport control is the button (tapping the name confused:
              nothing on the card LOOKED like play). */}
          <button
            type="button"
            className={`cs-btn cs-btn-sm ah-dj-zbtn ${z.djOn ? '' : 'cs-btn-outline'}`}
            disabled={inflight && !z.djOn}
            onClick={() => toggleZone(z)}
            aria-label={z.djOn ? `Stop DJ in ${z.name}` : `Play DJ in ${z.name}`}
          >
            {z.djOn ? '\u25A0' : '\u25B6'}
          </button>
          <span className={`ah-dj-zname ${z.djOn ? 'on' : ''}`}>{z.name}</span>
          <div className="ah-ctl-dim">
            <input
              type="range"
              min="0"
              max="100"
              step="2"
              value={drafts[z.address] ?? z.volume ?? 0}
              onChange={(e) => nudgeVolume(z, Number(e.target.value))}
              aria-label={`${z.name} volume`}
            />
            <span className="ah-ctl-pct">{drafts[z.address] ?? z.volume ?? 0}%</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// Media player (Apple TV / Sonos via ha_bridge): power, play/pause, volume.
// The Apple TV passes power + volume through to the TV itself over HDMI-CEC,
// so this card doubles as the TV remote's essentials — plus typed search
// (YouTube/Netflix deep links), an app launcher (tvOS apps are HA sources),
// and a fold-out D-pad remote. `hero` is the Media tab's expanded TV layout:
// remote always out, bigger now-playing, results as a thumbnail grid.
function MediaCard({ device, email, pending, onPending, hero = false }) {
  const busy = !!pending;
  const sentAt = useRef(null);
  const isTv = isTvDevice(device);
  const [query, setQuery] = useState('');
  const [searchApp, setSearchApp] = useState('YouTube');
  const [showRemote, setShowRemote] = useState(hero);
  const [results, setResults] = useState(null); // choose-mode YouTube picks
  const [searching, setSearching] = useState(false);
  const [searchErr, setSearchErr] = useState(null);
  // Hero browse rail: which chip's content is showing. Chips both launch
  // the app on the TV and select its rail here.
  const [selectedApp, setSelectedApp] = useState('YouTube');
  const { doc: feed } = useMediaFeed(hero && isTv ? feedIdFor(selectedApp) : null);

  useEffect(() => {
    if (busy && sentAt.current && (device.updatedAt || 0) >= sentAt.current - 3000) {
      onPending(device.address, false);
      sentAt.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device.updatedAt, busy]);

  const send = async (cmd, opts = {}) => {
    sentAt.current = Date.now();
    onPending(device.address, true);
    try {
      await sendDeviceCommand(device, cmd, { ...opts, email });
    } catch (err) {
      console.error('media command failed', err);
      onPending(device.address, false);
    }
  };

  // Query-style searches await the bridge's answer on the command doc
  // instead of using the optimistic-pending path.
  const runSearch = async (mode) => {
    const q = query.trim();
    if (!q || searching) return;
    setSearchErr(null);
    setResults(null);
    if (searchApp === 'YouTube' && mode !== 'choose') {
      send('yt_play', { query: q });
      setQuery('');
      return;
    }
    if (searchApp === 'Netflix') {
      send('open_url', { url: `nflx://www.netflix.com/search?q=${encodeURIComponent(q)}` });
      setQuery('');
      return;
    }
    setSearching(true);
    try {
      const isChoose = searchApp === 'YouTube';
      const id = await sendDeviceCommand(device, isChoose ? 'yt_search' : 'tv_search_all', { query: q, email });
      const out = await awaitCommandResult(id, { timeoutMs: 30000 });
      if (out?.status === 'done') {
        if (isChoose) setResults(out.result?.results || []);
        else setQuery('');
      } else {
        setSearchErr(out?.error || 'Search timed out — is the bridge up?');
      }
    } catch (err) {
      console.error('tv search failed', err);
      setSearchErr(String(err?.message || err));
    } finally {
      setSearching(false);
    }
  };

  const nowPlaying = [device.app, device.title, device.artist].filter(Boolean).join(' · ');
  const stateLabel = device.playing ? 'Playing' : device.isOn ? (device.state || 'On') : 'Off';

  // Named fragments so the hero layout can re-arrange them into two columns
  // (controls left, remote right) without duplicating any control logic.
  const head = (
    <div className="ah-ctl-head">
      <span className="ah-ctl-name" title={device.address}>{device.name}</span>
      <span className="ah-shade-state">{stateLabel}</span>
      <button
        type="button"
        className={`cs-btn cs-btn-sm ${device.isOn ? '' : 'cs-btn-outline'}`}
        disabled={busy}
        onClick={() => send(device.isOn ? 'off' : 'on')}
      >
        {device.isOn ? 'Turn off' : 'Turn on'}
      </button>
      {busy && <span className="ah-ctl-spinner" aria-label="working" />}
    </div>
  );

  const npBlock = hero ? (
    <div className={`ah-media-np ${nowPlaying ? '' : 'idle'}`} title={nowPlaying}>
      {nowPlaying || (device.isOn ? 'Nothing playing' : 'TV is off')}
    </div>
  ) : (
    nowPlaying && <div className="ah-media-np" title={nowPlaying}>{nowPlaying}</div>
  );

  const transport = (
    <div className="ah-ctl-row ah-media-btns">
      <button
        type="button"
        className="cs-btn cs-btn-outline cs-btn-sm"
        onClick={() => send(device.playing ? 'pause' : 'play')}
      >
        {device.playing ? 'Pause' : 'Play'}
      </button>
      <span className="ah-media-vol">
        <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => send('volume_down')}>
          Vol −
        </button>
        {device.volume != null && <span className="ah-media-volpct">{device.volume}%</span>}
        <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => send('volume_up')}>
          Vol +
        </button>
      </span>
    </div>
  );

  // Hero-only quick-launch chips: the owner's top apps, one tap to open.
  const topApps = hero
    ? TOP_TV_APPS
        .map((want) => (device.sourceList || []).find((s) => nbsp(s).toLowerCase() === want.toLowerCase()))
        .filter(Boolean)
        .slice(0, 5)
    : [];

  const appChips = topApps.length > 0 && (
    <div className="ah-tv-apps">
      {topApps.map((a) => (
        <button
          key={a}
          type="button"
          className={`cs-btn cs-btn-sm ${nbsp(selectedApp) === nbsp(a) ? '' : 'cs-btn-outline'}`}
          onClick={() => {
            send('app', { app: a });
            setSelectedApp(nbsp(a));
          }}
        >
          {nbsp(a)}
        </button>
      ))}
    </div>
  );

  // Browse rail for the selected chip. YouTube = the owner's subscriptions
  // (tap plays on the TV); TMDB apps = top picks (tap opens that title's
  // search on the TV — no app exposes direct-play).
  const openPick = (title) => {
    if (nbsp(selectedApp).toLowerCase() === 'netflix') {
      send('open_url', { url: `nflx://www.netflix.com/search?q=${encodeURIComponent(title)}` });
    } else {
      send('tv_search_all', { query: title });
    }
  };
  const rail = hero && isTv && feedIdFor(selectedApp) && (
    <div className="ah-tv-rail-wrap">
      <div className="ah-tv-rail-head">
        <span>{feed?.label || `On ${nbsp(selectedApp)}`}</span>
        {feed?.updatedAt && <span className="ah-tv-rail-ago">updated {agoLabel(feed.updatedAt)}</span>}
      </div>
      {feed === null && (
        <p className="cs-help">
          {nbsp(selectedApp).toLowerCase() === 'youtube'
            ? 'Not connected yet — the one-time YouTube account link (scripts/youtube-oauth.mjs) fills this with the latest from your subscriptions. Only your account data, never generic trending.'
            : 'Top picks appear once the (free) TMDB API key is added — SSM tmdb-api-key.'}
        </p>
      )}
      {feed?.kind === 'youtube' && (
        <div className="ah-tv-rail">
          {(feed.items || []).map((v) => (
            <button
              key={v.videoId}
              type="button"
              className="ah-tv-result ah-tv-rail-video"
              title={v.title}
              onClick={() => send('yt_play', { videoId: v.videoId })}
            >
              <span className="ah-tv-result-thumb">
                <img
                  src={v.thumbHq || v.thumb}
                  onError={(e) => {
                    if (v.thumbHq && e.target.src !== v.thumb) e.target.src = v.thumb;
                  }}
                  alt=""
                  loading="lazy"
                />
                <span className="ah-tv-result-play" aria-hidden>▶</span>
              </span>
              <span className="ah-tv-result-title">{v.title}</span>
              <span className="ah-tv-result-meta">
                {[v.channel, v.publishedAt ? agoLabel(Date.parse(v.publishedAt)) : null].filter(Boolean).join(' · ')}
              </span>
            </button>
          ))}
        </div>
      )}
      {feed?.kind === 'tmdb' && (
        <div className="ah-tv-rail">
          {(feed.items || []).map((t) => (
            <button
              key={`${t.type}-${t.title}`}
              type="button"
              className="ah-tv-poster"
              title={`${t.title} — opens this title's search on the TV`}
              onClick={() => openPick(t.title)}
            >
              <img src={t.poster} alt="" loading="lazy" />
              <span className="ah-tv-poster-title">{t.title}</span>
              <span className="ah-tv-result-meta">
                {[t.year, t.rating ? `★ ${t.rating}` : null].filter(Boolean).join(' · ')}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const tvControls = isTv && (
        <>
          <form
            className="ah-tv-search"
            onSubmit={(e) => {
              e.preventDefault();
              runSearch('top');
            }}
          >
            <input
              type="text"
              placeholder={
                searchApp === 'YouTube' ? 'Search YouTube…'
                  : searchApp === 'All apps' ? 'Search everything on the TV…'
                    : 'Search Netflix…'
              }
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <select
              value={searchApp}
              onChange={(e) => { setSearchApp(e.target.value); setResults(null); setSearchErr(null); }}
              aria-label="Search in"
            >
              {TV_SEARCH_TARGETS.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
            {searchApp === 'YouTube' ? (
              <>
                <button type="submit" className="cs-btn cs-btn-sm" disabled={!query.trim() || searching}>
                  Play top
                </button>
                <button
                  type="button"
                  className="cs-btn cs-btn-outline cs-btn-sm"
                  disabled={!query.trim() || searching}
                  onClick={() => runSearch('choose')}
                >
                  {searching ? '…' : 'Pick…'}
                </button>
              </>
            ) : (
              <button type="submit" className="cs-btn cs-btn-sm" disabled={!query.trim() || searching}>
                {searching ? '…' : searchApp === 'All apps' ? 'Search TV' : 'Go'}
              </button>
            )}
          </form>
          {searchErr && <div className="ah-tv-search-err">{searchErr}</div>}
          {results && (
            <div className="ah-tv-results">
              {results.length === 0 && <div className="ah-tv-search-err">No results.</div>}
              {results.map((r) => (
                <button
                  key={r.videoId}
                  type="button"
                  className="ah-tv-result"
                  title={r.title}
                  onClick={() => {
                    send('yt_play', { videoId: r.videoId });
                    setResults(null);
                    setQuery('');
                  }}
                >
                  <span className="ah-tv-result-thumb">
                    <img
                      src={r.thumbHq || r.thumb}
                      onError={(e) => {
                        if (r.thumbHq && e.target.src !== r.thumb) e.target.src = r.thumb;
                      }}
                      alt=""
                      loading="lazy"
                    />
                    {r.duration && <span className="ah-tv-result-dur">{r.duration}</span>}
                    <span className="ah-tv-result-play" aria-hidden>▶</span>
                  </span>
                  <span className="ah-tv-result-title">{r.title}</span>
                  {(r.channel || r.views) && (
                    <span className="ah-tv-result-meta">
                      {[r.channel, r.views].filter(Boolean).join(' · ')}
                    </span>
                  )}
                </button>
              ))}
              <button
                type="button"
                className="cs-btn cs-btn-outline cs-btn-sm ah-tv-results-cancel"
                onClick={() => setResults(null)}
              >
                Cancel
              </button>
            </div>
          )}
          <div className="ah-tv-tools">
            <select
              value=""
              onChange={(e) => e.target.value && send('app', { app: e.target.value })}
              aria-label="Open app"
            >
              <option value="">Open app…</option>
              {device.sourceList.map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
            {!hero && (
              <button
                type="button"
                className="cs-btn cs-btn-outline cs-btn-sm"
                onClick={() => setShowRemote((v) => !v)}
              >
                {showRemote ? 'Hide remote' : 'Remote'}
              </button>
            )}
          </div>
        </>
  );

  const remote = isTv && showRemote && (
    <div className="ah-tv-remote">
      {[
        ['', 'up', ''],
        ['left', 'select', 'right'],
        ['', 'down', ''],
        ['menu', '', 'home'],
      ].map((row, i) => (
        <div key={i} className="ah-tv-remote-row">
          {row.map((btn, j) =>
            btn ? (
              <button
                key={j}
                type="button"
                className="cs-btn cs-btn-outline cs-btn-sm"
                onClick={() => send('remote', { button: btn })}
              >
                {{ up: '▲', down: '▼', left: '◀', right: '▶', select: 'OK', menu: 'Menu', home: 'Home' }[btn]}
              </button>
            ) : (
              <span key={j} className="ah-tv-remote-gap" />
            )
          )}
        </div>
      ))}
    </div>
  );

  if (hero) {
    return (
      <div className={`ah-ctl-card ah-media-hero ${device.isOn ? 'on' : ''} ${busy ? 'busy' : ''}`}>
        {head}
        {npBlock}
        <div className="ah-media-hero-cols">
          <div className="ah-media-hero-main">
            {transport}
            {appChips}
            {rail}
            {tvControls}
          </div>
          {remote && <div className="ah-media-hero-side">{remote}</div>}
        </div>
      </div>
    );
  }

  return (
    <div className={`ah-ctl-card ${device.isOn ? 'on' : ''} ${busy ? 'busy' : ''}`}>
      {head}
      {npBlock}
      {transport}
      {tvControls}
      {remote}
    </div>
  );
}

// Two-tone chime for camera motion alerts — WebAudio so there's no asset to
// load; only fires for cameras whose bell is armed, while the app is open.
// (Ring's own app still does real push notifications.)
let chimeCtx = null;
const playChime = () => {
  try {
    chimeCtx = chimeCtx || new (window.AudioContext || window.webkitAudioContext)();
    const ctx = chimeCtx;
    [[880, 0], [1175, 0.18]].forEach(([freq, at]) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = freq;
      osc.connect(gain).connect(ctx.destination);
      gain.gain.setValueAtTime(0.15, ctx.currentTime + at);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + at + 0.4);
      osc.start(ctx.currentTime + at);
      osc.stop(ctx.currentTime + at + 0.45);
    });
  } catch (err) {
    console.error('chime failed', err);
  }
};

// Ring camera (bridge/ring_snap.py): latest snapshot thumbnail, motion badge,
// and a Live button that opens the go2rtc web player on the house box —
// works on the house Wi-Fi or anywhere with Tailscale on. The bell arms the
// in-app motion chime for this camera.
function CameraCard({ device, alertOn, onAlertToggle }) {
  const motion = !!device.motion;
  return (
    <div className={`ah-cam-card ${motion ? 'motion' : ''}`}>
      <div className="ah-cam-thumb">
        {device.snapshotB64 ? (
          <img src={device.snapshotB64} alt={device.name} />
        ) : (
          <span className="ah-cam-nosnap">No snapshot yet — arrives on next motion</span>
        )}
        {motion && <span className="ah-cam-motion">MOTION</span>}
        {device.reachable === false && <span className="ah-cam-offline">offline</span>}
        {device.snapshotAt && (
          <span className="ah-cam-age">{agoLabel(device.snapshotAt)}</span>
        )}
      </div>
      <div className="ah-cam-row">
        <span className="ah-ctl-name" title={device.address}>{device.name}</span>
        <span className="ah-ctl-scene-btns">
          <button
            type="button"
            className={`cs-btn cs-btn-sm ${alertOn ? '' : 'cs-btn-outline'}`}
            title={alertOn ? 'Motion chime armed (while app is open)' : 'Arm motion chime'}
            onClick={onAlertToggle}
          >
            {alertOn ? 'Chime on' : 'Chime'}
          </button>
          {device.liveUrl && (
            <a className="cs-btn cs-btn-sm" href={device.liveUrl} target="_blank" rel="noreferrer">
              Live
            </a>
          )}
        </span>
      </div>
      <div className="ah-cam-meta">
        {device.motionAt ? `Last motion ${agoLabel(device.motionAt)}` : 'No motion seen yet'}
        {device.battery != null ? ` · battery ${device.battery}%` : ''}
      </div>
    </div>
  );
}

// Pool/spa heat: current temp, a "Spa/Pool Mode" toggle, and a debounced
// setpoint stepper. The mode toggle drives the heater AND that body's pump
// together (the temp sensor only reads under flow, and heat needs
// circulation anyway). Same optimistic + updatedAt-confirmation pattern as
// DeviceCard.
function AquaThermostat({ device, email, pending, onPending }) {
  const busy = !!pending;
  const [draftSp, setDraftSp] = useState(null);
  const [optimistic, setOptimistic] = useState(null);
  const stepTimer = useRef(null);
  const sentAt = useRef(null);

  useEffect(() => {
    if (busy && sentAt.current && (device.updatedAt || 0) >= sentAt.current - 3000) {
      onPending(device.address, false);
      sentAt.current = null;
      setOptimistic(null);
      setDraftSp(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device.updatedAt, busy]);
  useEffect(() => {
    if (!busy) setOptimistic(null);
  }, [busy]);

  const send = async (cmd, value) => {
    if (device.demo) return; // preview device — simulate locally (see DeviceCard)
    sentAt.current = Date.now();
    onPending(device.address, true);
    try {
      await sendAquaCommand(device.address, cmd, { value, email });
    } catch (err) {
      console.error('aqua command failed', err);
      onPending(device.address, false);
      setOptimistic(null);
      setDraftSp(null);
    }
  };

  // "Spa Mode" / "Pool Mode": heater on/off + that body's pump on/off in one
  // gesture. Address shape: aqua:{serial}:{spa|pool}_set_point -> _pump.
  const body = /spa_set_point$/.test(device.address || '') ? 'Spa' : 'Pool';
  const pumpAddress = (device.address || '').replace(/_set_point$/, '_pump');
  const sendMode = async (on) => {
    if (device.demo) return;
    sentAt.current = Date.now();
    onPending(device.address, true);
    try {
      await sendAquaCommand(device.address, on ? 'on' : 'off', { email });
      await sendAquaCommand(pumpAddress, on ? 'on' : 'off', { email });
    } catch (err) {
      console.error('aqua mode command failed', err);
      onPending(device.address, false);
      setOptimistic(null);
    }
  };

  const heatOn = optimistic != null ? optimistic : device.isOn;
  const sp = draftSp != null ? draftSp : device.setpoint;
  const step = (delta) => {
    const min = device.minTemp || 34;
    const max = device.maxTemp || 104;
    const next = Math.max(min, Math.min(max, (sp || 75) + delta));
    setDraftSp(next);
    clearTimeout(stepTimer.current);
    stepTimer.current = setTimeout(() => send('setTemp', next), 700);
  };

  return (
    <div className={`ah-ctl-card ah-aqua-thermo ${heatOn ? 'on' : ''}`}>
      <div className="ah-ctl-head">
        <span className="ah-ctl-name" title={device.address}>{`${body} Mode`}</span>
        {heatOn && <span className="ah-aqua-heating">heating</span>}
      </div>
      <div className="ah-aqua-temp-row">
        <span className="ah-aqua-now">
          {device.temp != null ? (
            `${Math.round(device.temp)}°${device.unit || ''}`
          ) : device.lastTemp != null ? (
            <>
              {`${Math.round(device.lastTemp)}°${device.unit || ''}`}
              {device.lastTempAt && <span className="ah-aqua-ago"> {agoLabel(device.lastTempAt)}</span>}
            </>
          ) : (
            '—'
          )}
        </span>
        <span className="ah-aqua-arrow">→</span>
        <div className="ah-aqua-set">
          <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" disabled={busy && draftSp == null} onClick={() => step(-1)}>
            −
          </button>
          <span className="ah-aqua-sp">{sp != null ? `${Math.round(sp)}°` : '—'}</span>
          <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" disabled={busy && draftSp == null} onClick={() => step(1)}>
            +
          </button>
        </div>
      </div>
      <div className="ah-ctl-row">
        <button
          type="button"
          className={`ah-ctl-toggle ${heatOn ? 'on' : ''}`}
          aria-label={heatOn ? `Turn ${body.toLowerCase()} mode off` : `Turn ${body.toLowerCase()} mode on`}
          onClick={() => {
            const target = !heatOn;
            setOptimistic(target);
            sendMode(target);
          }}
        >
          <span className="ah-ctl-knob" />
        </button>
        <span className="cs-help" style={{ margin: 0 }}>heat + pump to {sp != null ? `${Math.round(sp)}°` : 'setpoint'}</span>
        {busy && <span className="ah-ctl-spinner" aria-label="working" />}
      </div>
    </div>
  );
}

// One pool controller's cards (heat / readings / equipment). Non-primary
// systems (the owner's other house) start collapsed; factory-named aux
// channels hide behind their own toggle.
function AquaSystemSection({ system, email, pendingMap, onPending }) {
  const [open, setOpen] = useState(system.primary);
  const [showUnassigned, setShowUnassigned] = useState(false);
  const deviceCount =
    system.thermostats.length + system.switches.length + system.unassigned.length;

  return (
    <div className="ah-aqua-system">
      <div className="ah-ctl-dead-head">
        <h2 className="ah-ctl-section">
          {system.name}
          {!system.primary && ' (other house)'}
        </h2>
        {!system.primary && (
          <button
            type="button"
            className="cs-btn cs-btn-outline cs-btn-sm"
            onClick={() => setOpen(!open)}
          >
            {open ? 'Hide' : `Show (${deviceCount})`}
          </button>
        )}
      </div>
      {open && (
        <>
          {system.thermostats.length > 0 && (
            <div className="ah-ctl-grid ah-aqua-thermo-grid">
              {system.thermostats.map((d) => (
                <AquaThermostat
                  key={d.id}
                  device={d}
                  email={email}
                  pending={pendingMap[d.address]}
                  onPending={onPending}
                />
              ))}
            </div>
          )}
          {system.sensors.length > 0 && (
            <div className="ah-aqua-sensors">
              {system.sensors.map((d) => (
                <div key={d.id} className="ah-aqua-sensor" title={d.address}>
                  <span className="ah-aqua-sensor-name">{d.name}</span>
                  <span className="ah-aqua-sensor-val">
                    {d.aquaType === 'binary' ? (
                      d.isOn ? 'on' : 'off'
                    ) : d.value ? (
                      `${d.value}${d.unit ? `°${d.unit}` : ''}`
                    ) : d.lastValue ? (
                      <>
                        {`${d.lastValue}${d.unit ? `°${d.unit}` : ''}`}
                        {d.lastValueAt && <span className="ah-aqua-ago"> {agoLabel(d.lastValueAt)}</span>}
                      </>
                    ) : (
                      '—'
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
          {system.switches.length > 0 && (
            <div className="ah-ctl-grid">
              {system.switches.map((d) => (
                <DeviceCard
                  key={d.id}
                  device={d}
                  email={email}
                  pending={pendingMap[d.address]}
                  onPending={onPending}
                />
              ))}
            </div>
          )}
          {system.unassigned.length > 0 && (
            <>
              <button
                type="button"
                className="cs-btn cs-btn-outline cs-btn-sm"
                style={{ marginTop: 8 }}
                onClick={() => setShowUnassigned(!showUnassigned)}
              >
                {showUnassigned ? 'Hide' : 'Show'} unassigned aux ({system.unassigned.length})
              </button>
              {showUnassigned && (
                <div className="ah-ctl-grid" style={{ marginTop: 8 }}>
                  {system.unassigned.map((d) => (
                    <DeviceCard
                      key={d.id}
                      device={d}
                      email={email}
                      pending={pendingMap[d.address]}
                      onPending={onPending}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

// App-side automations — the backend workers acting on the house on their
// own (dimmer heals, auto-off rules for glitchy switches, pool/spa temp
// sampling). Listed here so they're never invisible: every rule lives in
// the live config doc (config/app, controls.*) and can be tweaked or
// deleted there — changes apply within ~1 min, no deploy. Display fallbacks
// mirror the backend defaults in backend/shared/liveConfig.js.
function AppAutomations({ config, nameOf, snoozedUntil }) {
  const c = config?.controls || {};
  const healEnabled = c.healEnabled !== false;
  const heals = Array.isArray(c.heals) ? c.heals : [];
  const sampleEnabled = c.aquaSampleEnabled !== false;
  const interval = c.aquaSampleIntervalMin ?? 45;
  const runSec = c.aquaSampleRunSec ?? 180;
  const h0 = c.aquaSampleHourStart ?? 7;
  const h1 = c.aquaSampleHourEnd ?? 22;

  const rows = [];
  heals.forEach((h) => {
    if (!h?.device) return;
    const hoursLabel = Array.isArray(h.offAt)
      ? h.offAt.map((x) => `${x}:00`).join(', ')
      : '';
    rows.push({
      key: `heal-${h.device}`,
      on: healEnabled,
      title: hoursLabel
        ? `Scheduled off — ${nameOf(h.device)} at ${hoursLabel}`
        : h.off
          ? `Auto-off — ${nameOf(h.device)}`
          : `Auto-heal — ${nameOf(h.device)} → ${h.pct}%${h.strikePct ? ` (strike ${h.strikePct}%)` : ''}`,
      detail: h.note
        || (hoursLabel
          ? 'Sent OFF at each listed hour daily; turning it back on sticks until the next one.'
          : h.off
            ? 'Forced off whenever it turns on (glitching switch).'
            : 'Snaps back to the target brightness whenever left on at another level.'),
    });
  });
  if (snoozedUntil) {
    rows.push({
      key: 'snooze',
      on: true,
      title: `Snooze active — scheduled off sweeps paused until ${timeLabel(snoozedUntil)}`,
      detail: 'Set from a favorite tile (Zz). Heaters and spa won\u2019t be auto-shut while it runs; tap Zz again to cancel.',
    });
  }
  rows.push({
    key: 'aqua-sampler',
    on: sampleEnabled,
    title: `Pool/spa temp sampler — every ${interval} min, ${h0}:00–${h1}:00`,
    detail: `Water temps only read under flow; when a reading goes stale the pump runs for ${runSec}s, then shuts off. Skips pumps already in use.`,
  });

  return (
    <>
      <h2 className="ah-ctl-section">App automations</h2>
      <div className="ah-pgm-list">
        {rows.map((r) => (
          <div key={r.key} className={`ah-pgm ${r.on ? '' : 'disabled'}`}>
            <div className="ah-pgm-head" style={{ cursor: 'default' }}>
              <span className={`ah-pgm-dot ${r.on ? 'true' : ''}`} title={r.on ? 'active' : 'disabled'} />
              <span className="ah-pgm-name">{r.title}</span>
              {!r.on && <span className="ah-pgm-badge">disabled</span>}
            </div>
            <p className="cs-help" style={{ margin: '4px 12px 10px' }}>{r.detail}</p>
          </div>
        ))}
      </div>
      <p className="cs-help">
        These run in the backend (not on the ISY) and act through the same command
        queue as the buttons on this page. Each lives in the live-config doc
        (Firestore <code>config/app</code> → <code>controls.*</code>) — edit or delete
        a rule there and it applies within a minute, no deploy. <code>healEnabled</code>{' '}
        / <code>aquaSampleEnabled</code> are the master switches.
      </p>
    </>
  );
}

function ProgramRow({ program, email, pending, onPending }) {
  const [open, setOpen] = useState(false);
  const busy = !!pending;

  const send = async (cmd) => {
    onPending(program.pgmId, true);
    try {
      await sendProgramCommand(program.pgmId, cmd, { email });
    } catch (err) {
      console.error('program command failed', err);
      onPending(program.pgmId, false);
    }
  };

  const logicBlock = (title, lines) =>
    lines && lines.length > 0 ? (
      <div className="ah-pgm-block">
        <span className="ah-pgm-kw">{title}</span>
        <div>
          {lines.map((line, i) => (
            <div key={i} className="ah-pgm-line">{line}</div>
          ))}
        </div>
      </div>
    ) : null;

  return (
    <div className={`ah-pgm ${program.enabled ? '' : 'disabled'}`}>
      <button type="button" className="ah-pgm-head" onClick={() => setOpen(!open)}>
        <span className={`ah-pgm-dot ${program.statusTrue ? 'true' : ''}`} title={`If condition is currently ${program.statusTrue ? 'TRUE' : 'false'}`} />
        <span className="ah-pgm-name">{program.name}</span>
        {!program.enabled && <span className="ah-pgm-badge">disabled</span>}
        {program.running && program.running !== 'idle' && (
          <span className="ah-pgm-badge run">{program.running}</span>
        )}
        <span className="ah-pgm-next">
          {program.nextScheduledRunTime ? `next ${program.nextScheduledRunTime}` : ''}
        </span>
        <span className="ah-pgm-caret">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div className="ah-pgm-body">
          {logicBlock('If', program.ifText)}
          {logicBlock('Then', program.thenText)}
          {logicBlock('Else', program.elseText)}
          {!((program.ifText || []).length || (program.thenText || []).length) && (
            <p className="cs-help">No logic decoded for this program.</p>
          )}
          {program.comment && <p className="ah-pgm-comment">{program.comment}</p>}
          <div className="ah-pgm-meta">
            {program.lastRunTime && <span>last run {program.lastRunTime}</span>}
          </div>
          <div className="ah-pgm-actions">
            <button className="cs-btn cs-btn-sm" disabled={busy} onClick={() => send('runThen')}>
              Run Then
            </button>
            <button className="cs-btn cs-btn-outline cs-btn-sm" disabled={busy} onClick={() => send('runElse')}>
              Run Else
            </button>
            <button className="cs-btn cs-btn-outline cs-btn-sm" disabled={busy} onClick={() => send('run')}>
              Run If
            </button>
            <button
              className="cs-btn cs-btn-outline cs-btn-sm"
              disabled={busy}
              onClick={() => send(program.enabled ? 'disable' : 'enable')}
            >
              {program.enabled ? 'Disable' : 'Enable'}
            </button>
            {busy && <span className="ah-ctl-spinner" aria-label="working" />}
          </div>
        </div>
      )}
    </div>
  );
}

// Network inventory (networkDevices collection): the LAN roster, grouped by
// connection type. Identity fields are documentation; the live dot comes from
// the controlDevices heartbeat doc named in heartbeatDoc, so bridged devices
// show real liveness and everything else is just the book of record.
function NetworkTab({ devices, hbDocs }) {
  if (devices === null) return <p className="cs-help">Loading network inventory…</p>;
  if (!devices.length) {
    return <p className="cs-help">No network devices recorded yet.</p>;
  }
  const liveness = (dev) => {
    if (!dev.heartbeatDoc) return null;
    // Dedicated per-doc subscriptions: heartbeat docs have no `name` field,
    // so the name-ordered controlDevices collection query omits them.
    const hb = hbDocs[dev.heartbeatDoc];
    if (!hb) return { ok: false, label: 'no signal' };
    const fresh = Date.now() - (hb.heartbeat || 0) < BRIDGE_STALE_MS;
    const connected = hb.connected !== false && hb.isyConnected !== false;
    return fresh && connected
      ? { ok: true, label: 'online' }
      : { ok: false, label: fresh ? 'bridge up, device down' : 'offline' };
  };
  const groups = [
    { key: 'ethernet', label: 'Ethernet' },
    { key: 'wifi', label: 'Wi-Fi' },
  ];
  return (
    <>
      <NetworkDiagram devices={devices} liveness={liveness} />
      {groups.map(({ key, label }) => {
        const list = devices.filter((d) => (d.conn || 'ethernet') === key);
        if (!list.length) return null;
        return (
          <div key={key}>
            <h2 className="ah-ctl-section">{label}</h2>
            <div className="ah-net-list">
              {list.map((dev) => {
                const live = liveness(dev);
                return (
                  <div key={dev.id} className={`ah-net-row ${dev.planned ? 'planned' : ''}`}>
                    <span
                      className={`ah-net-dot ${
                        dev.planned ? 'planned' : live ? (live.ok ? 'ok' : 'down') : 'none'
                      }`}
                      title={dev.planned ? 'planned' : live ? live.label : 'not monitored'}
                    />
                    <div className="ah-net-main">
                      <div className="ah-net-name">
                        {dev.name}
                        {dev.planned && <span className="ah-net-tag">planned</span>}
                        {live && !dev.planned && (
                          <span className={`ah-net-tag ${live.ok ? 'ok' : 'down'}`}>{live.label}</span>
                        )}
                      </div>
                      <div className="ah-net-meta">
                        {[dev.ssid && `SSID ${dev.ssid}`, dev.ip, dev.mac, dev.firmware, dev.runs]
                          .filter(Boolean)
                          .join(' · ') || '—'}
                      </div>
                      {dev.notes && <div className="ah-net-notes">{dev.notes}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      <h2 className="ah-ctl-section">Remote access — how it works</h2>
      <div className="ah-net-list">
        <div className="ah-net-row">
          <div className="ah-net-main">
            <div className="ah-net-name">From anywhere: connect Tailscale, then use plain LAN URLs</div>
            <div className="ah-net-notes">
              Router admin: http://192.168.50.1 (or https://192.168.50.1:8443 — the
              self-signed certificate warning is expected, click through). Home
              Assistant: http://192.168.50.184:8123. Any device on 192.168.50.x works
              the same way.
            </div>
          </div>
        </div>
        <div className="ah-net-row">
          <div className="ah-net-main">
            <div className="ah-net-name">Why it works</div>
            <div className="ah-net-notes">
              The housebox Pi is a Tailscale subnet router advertising 192.168.50.0/24.
              Your device builds an end-to-end encrypted WireGuard tunnel to the Pi,
              which hands traffic to the LAN as if you were home. Even over http URLs,
              everything crossing the internet is encrypted by the tunnel.
            </div>
          </div>
        </div>
        <div className="ah-net-row">
          <div className="ah-net-main">
            <div className="ah-net-name">Why the public can&apos;t get in</div>
            <div className="ah-net-notes">
              192.168.50.x addresses don&apos;t exist on the public internet and no
              router ports are forwarded — there is no login page to find or password
              to guess. Access requires a device enrolled in the tailnet (approved in
              the Tailscale admin console) holding its cryptographic device key. To
              add a phone: install the Tailscale app and sign in to the tailnet.
            </div>
          </div>
        </div>
      </div>
      <h2 className="ah-ctl-section">House remote (voice) — how it works</h2>
      <div className="ah-net-list">
        <div className="ah-net-row">
          <div className="ah-net-main">
            <div className="ah-net-name">Using the M5StickS3 remote</div>
            <div className="ah-net-notes">
              Tap the front button, speak, then just stop talking — it detects the
              pause and transcribes automatically (tapping again while listening
              cancels). Hold the front button ~1s to turn all lamps off; the side
              button toggles the Morph lamp. It answers out loud through its
              speaker and shows the conversation on screen.
            </div>
          </div>
        </div>
        <div className="ah-net-row">
          <div className="ah-net-main">
            <div className="ah-net-name">It shares the app assistant&apos;s brain</div>
            <div className="ah-net-notes">
              Speech-to-text (Whisper) and the reply voice (Piper) run locally on
              the housebox Pi — audio never leaves the LAN. The transcript is
              forwarded to the same house voice assistant the app uses, so scenes
              (&quot;party mode&quot;), device control, and house knowledge all work
              identically by voice or in-app.
            </div>
          </div>
        </div>
        <div className="ah-net-row">
          <div className="ah-net-main">
            <div className="ah-net-name">Speak naturally — it interprets</div>
            <div className="ah-net-notes">
              Loose phrasing works: it recovers intent from imperfect
              transcripts (&quot;child mode&quot; → Chill scene), knows each
              device&apos;s live state, treats near-twin names like the three
              patio string-light switches as one fixture, and does relative
              dimming (&quot;dim the kitchen by two clicks&quot;). Actions fire
              in ~5 s with the spoken reply a few seconds behind; commands
              dispatch immediately rather than waiting for confirmation, so
              on rare misses just ask again.
            </div>
          </div>
        </div>
        <div className="ah-net-row">
          <div className="ah-net-main">
            <div className="ah-net-name">Charging and limits</div>
            <div className="ah-net-notes">
              Near-field mic — talk within arm&apos;s reach, not across the room.
              The 250 mAh battery is small: keep it on USB-C for always-ready use.
              A short press wakes it; avoid long-pressing the front button while
              plugging in (that enters firmware-flash mode — unplug and replug to
              recover).
            </div>
          </div>
        </div>
      </div>
      <p className="cs-help" style={{ marginTop: 16 }}>
        The book of record for everything on the house LAN. Live dots reflect the
        Firestore bridge heartbeats; the rest is curated documentation (also kept in
        the project brain). Port budget: 16-port closet switch recommended — 8 ports
        committed on day one.
      </p>
    </>
  );
}

export default function Controls() {
  const { user } = useAuth();
  const { docs, error } = useControlDevices();
  const { doc: bridge } = useBridgeStatus();
  const { doc: aquaBridge } = useAquaBridgeStatus();
  const { doc: haBridge } = useHaBridgeStatus();
  const { doc: shadeBridge } = useShadeBridgeStatus();
  const { doc: ringBridge } = useRingBridgeStatus();
  const { doc: camAlerts } = useCameraAlerts();
  const { docs: programs } = useControlPrograms();
  const { docs: layout } = useControlLayout();
  const { doc: favDoc } = useControlFavorites();
  const { doc: snoozeDoc } = useAutoOffSnooze();
  const { doc: liveCfg } = useLiveConfig();
  const { docs: netDevices } = useNetworkDevices();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TABS.some((t) => t.id === searchParams.get('tab'))
    ? searchParams.get('tab')
    : 'devices';

  // address/pgmId -> timeoutId while a command is in flight; cleared the
  // moment the corresponding doc updates, or by this fallback if the bridge
  // is down and no confirmation ever arrives.
  const [pendingMap, setPendingMap] = useState({});

  const setPending = (key, on) => {
    setPendingMap((prev) => {
      const next = { ...prev };
      if (on) {
        clearTimeout(next[key]);
        next[key] = setTimeout(
          () => setPendingMap((p) => {
            const q = { ...p };
            delete q[key];
            return q;
          }),
          5000
        );
      } else {
        clearTimeout(next[key]);
        delete next[key];
      }
      return next;
    });
  };

  // Map-dot confirmations: clear a device's pending pulse as soon as its doc
  // refreshes (DeviceCard has its own per-card version of this).
  const lastUpdated = useRef({});
  useEffect(() => {
    (docs || []).forEach((d) => {
      const prev = lastUpdated.current[d.address];
      if (prev !== undefined && prev !== d.updatedAt && pendingMap[d.address]) {
        setPending(d.address, false);
      }
      lastUpdated.current[d.address] = d.updatedAt;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docs]);

  const { scenes, devices, unresponsive, aqua, ble, media, shades, cameras, climate, fans } = useMemo(() => {
    const list = (docs || []).filter((d) => !d.id.startsWith('_'));
    const isyList = list.filter((d) => !['aqualink', 'ha', 'shade', 'ring'].includes(d.vendor));
    const isyDevices = isyList.filter((d) => d.kind !== 'scene');
    return {
      scenes: isyList.filter((d) => d.kind === 'scene'),
      // ISY nodes that report no status (reachable: false) are parked in
      // their own hidden section so dead/unpowered nodes don't clutter
      // the working controls.
      devices: isyDevices.filter((d) => d.reachable !== false),
      unresponsive: isyDevices.filter((d) => d.reachable === false),
      aqua: list.filter((d) => d.vendor === 'aqualink'),
      // Bluetooth lights mirrored from Home Assistant on the house box.
      ble: list.filter((d) => d.vendor === 'ha' && d.kind !== 'media' && d.kind !== 'climate' && d.kind !== 'fan'),
      // AC / thermostats via the same HA mirror (Gree master-bedroom unit).
      climate: list.filter((d) => d.vendor === 'ha' && d.kind === 'climate'),
      // Purifier fans via the same HA mirror (Dyson master-bedroom unit).
      fans: list.filter((d) => d.vendor === 'ha' && d.kind === 'fan'),
      // Media players via the same HA mirror: Apple TV (relays TV
      // power/volume over HDMI-CEC) + Sonos speakers.
      media: list.filter((d) => d.vendor === 'ha' && d.kind === 'media'),
      // Motorized shades via the Rollease Pulse 2 hub (shade_bridge.py).
      shades: list.filter((d) => d.vendor === 'shade'),
      // Ring cameras mirrored by ring_snap.py (thumbnails + motion).
      cameras: list.filter((d) => d.vendor === 'ring'),
    };
  }, [docs]);
  const [showUnresponsive, setShowUnresponsive] = useState(false);

  // Cameras: this house first; the other Ring location renders collapsed
  // (same idea as the second pool controller).
  const [showOtherCams, setShowOtherCams] = useState(false);
  const homeCams = cameras.filter((d) => /amalfi/i.test(d.locationName || ''));
  const otherCams = cameras.filter((d) => !/amalfi/i.test(d.locationName || ''));

  // Motion chime: when an armed camera's motion flips on, play the two-tone
  // chime (in-app only — the tab must be open).
  const prevMotion = useRef({});
  useEffect(() => {
    const armed = camAlerts?.enabled || {};
    (docs || []).forEach((d) => {
      if (d.vendor !== 'ring') return;
      if (prevMotion.current[d.id] === false && d.motion === true && armed[d.id]) playChime();
      prevMotion.current[d.id] = !!d.motion;
    });
  }, [docs, camAlerts]);

  const toggleCamAlert = (id) => {
    const enabled = { ...(camAlerts?.enabled || {}) };
    enabled[id] = !enabled[id];
    saveCameraAlerts(enabled).catch((err) => console.error('camera alerts save failed', err));
  };

  // Pool groupings, per SYSTEM — the account can carry several pool
  // controllers (this house's "AMALFI" + the owner's other house). The
  // system whose name matches the house is primary (expanded, on the map);
  // the rest render collapsed. Raw *_heater switches are hidden — the
  // thermostat cards wrap them (heat toggle + setpoint) — and factory-named
  // "Aux N"/"Aux VN" channels (nothing wired or labeled) are parked behind
  // a per-system toggle.
  const aquaSystems = useMemo(() => {
    const isHeater = (d) => /_heater$/.test(d.address || '');
    const isUnassigned = (d) => /^(aux ?v?\d+|extra aux)$/i.test(d.name || '');
    const names = [...new Set(aqua.map((d) => d.system || 'Pool'))];
    names.sort((a, b) => {
      const pa = /amalfi/i.test(a) ? 0 : 1;
      return pa - (/amalfi/i.test(b) ? 0 : 1) || a.localeCompare(b);
    });
    return names.map((name, i) => {
      const list = aqua.filter((d) => (d.system || 'Pool') === name);
      return {
        name,
        primary: i === 0,
        thermostats: list.filter((d) => d.aquaType === 'thermostat'),
        switches: list.filter((d) => d.aquaType === 'switch' && !isHeater(d) && !isUnassigned(d)),
        unassigned: list.filter((d) => d.aquaType === 'switch' && !isHeater(d) && isUnassigned(d)),
        sensors: list.filter((d) => d.kind === 'sensor'),
      };
    });
  }, [aqua]);
  const primaryAqua = aquaSystems.find((s) => s.primary);

  const programGroups = useMemo(() => {
    const real = (programs || []).filter((p) => !p.folder && p.id !== '_meta');
    const groups = {};
    real.forEach((p) => {
      const key = p.path || 'Programs';
      (groups[key] = groups[key] || []).push(p);
    });
    Object.values(groups).forEach((list) => list.sort((a, b) => a.name.localeCompare(b.name)));
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [programs]);

  if (!canUseControls(user)) {
    return (
      <div className="cs-page">
        <h1>Controls</h1>
        <p className="cs-help">This page is restricted to the house owner.</p>
      </div>
    );
  }

  const bridgeOnline = bridge && Date.now() - (bridge.heartbeat || 0) < BRIDGE_STALE_MS;
  const bleOnline =
    haBridge && Date.now() - (haBridge.heartbeat || 0) < BRIDGE_STALE_MS && haBridge.connected;
  const shadeOnline =
    shadeBridge && Date.now() - (shadeBridge.heartbeat || 0) < BRIDGE_STALE_MS && shadeBridge.connected;
  const ringOnline =
    ringBridge && Date.now() - (ringBridge.heartbeat || 0) < BRIDGE_STALE_MS && ringBridge.connected;

  const toggleDevice = (device) => {
    if (device.demo) return; // preview device — nothing behind it yet
    setPending(device.address, true);
    sendDeviceCommand(device, device.isOn ? 'off' : 'on', { email: user.email }).catch(
      (err) => {
        console.error('command failed', err);
        setPending(device.address, false);
      }
    );
  };

  // Quick-access favorites: ordered controlDevices ids, curated via the star
  // on each card. Ids that no longer resolve are silently skipped.
  const favIds = favDoc?.ids || [];
  const favTiles = favIds
    .map((id) => (docs || []).find((d) => d.id === id))
    .filter(Boolean);
  const toggleFav = (id) => {
    const ids = favIds.includes(id) ? favIds.filter((x) => x !== id) : [...favIds, id];
    saveControlFavorites(ids).catch((err) => console.error('favorites save failed', err));
  };

  // Scheduled auto-off sweeps (offAt heal rules) + the shared 5h snooze.
  const sweepIds = new Set(
    (liveCfg?.controls?.heals || [])
      .filter((h) => Array.isArray(h?.offAt) && h.offAt.length)
      .map((h) => h.device)
  );
  const snoozedUntil = snoozeDoc?.until > Date.now() ? snoozeDoc.until : null;
  const toggleSnooze = () => {
    saveAutoOffSnooze(snoozedUntil ? 0 : Date.now() + 5 * 3600 * 1000, user.email).catch((err) =>
      console.error('snooze save failed', err)
    );
  };

  return (
    <div className="cs-page">
      <div className="ah-ctl-header">
        <h1>Controls</h1>
        <span className={`ah-ctl-bridge ${bridgeOnline ? 'ok' : 'down'}`}>
          {bridgeOnline
            ? `Bridge online · ${bridge.deviceCount || 0} nodes`
            : 'Bridge offline'}
        </span>
      </div>

      {error && <p className="cs-help">Error: {error}</p>}

      {!bridgeOnline && (
        <p className="cs-help">
          The ISY bridge isn&apos;t reporting — it runs on ECS Fargate (see bridge/README.md).
          Device state below may be stale and commands will wait until it reconnects.
        </p>
      )}

      <div className="ah-ctl-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`ah-ctl-tab ${tab === t.id ? 'active' : ''}`}
            onClick={() => setSearchParams(t.id === 'devices' ? {} : { tab: t.id })}
          >
            {t.label}
          </button>
        ))}
      </div>

      {docs === null && <p className="cs-help">Loading devices…</p>}

      {tab === 'map' && docs !== null && (
        <>
          {/* unresponsive ISY nodes are excluded — their dots/tray chips
              would just be dead weight on the map */}
          <HouseMap
            devices={devices.concat(primaryAqua ? primaryAqua.switches : [], ble.filter((d) => d.reachable !== false))}
            layout={layout}
            onToggle={toggleDevice}
            pendingMap={pendingMap}
          />
          {scenes.length > 0 && (
            <>
              <h2 className="ah-ctl-section">Scenes</h2>
              <div className="ah-ctl-grid">
                {scenes.map((d) => (
                  <DeviceCard
                    key={d.id}
                    device={d}
                    email={user.email}
                    pending={pendingMap[d.address]}
                    onPending={setPending}
                  />
                ))}
              </div>
            </>
          )}
        </>
      )}

      {tab === 'devices' && (
        <>
          {docs !== null && !devices.length && !scenes.length && (
            <p className="cs-help">No devices yet — they appear after the bridge&apos;s first sync.</p>
          )}
          {favTiles.length > 0 ? (
            <>
              <h2 className="ah-ctl-section">Favorites</h2>
              <div className="ah-fav-grid">
                {favTiles.map((d) => (
                  <FavTile
                    key={d.id}
                    device={d}
                    email={user.email}
                    pending={pendingMap[d.address]}
                    onPending={setPending}
                    sweep={sweepIds.has(d.id)}
                    snoozedUntil={snoozedUntil}
                    onSnooze={toggleSnooze}
                  />
                ))}
              </div>
            </>
          ) : (
            docs !== null && (
              <p className="cs-help">
                No favorites yet — star devices on the other tabs and they show up here.
              </p>
            )
          )}
        </>
      )}

      {tab === 'lights' && (
        <>
          {scenes.length > 0 && (
            <>
              <h2 className="ah-ctl-section">Scenes</h2>
              <div className="ah-ctl-grid">
                {scenes.map((d) => (
                  <DeviceCard
                    key={d.id}
                    device={d}
                    email={user.email}
                    pending={pendingMap[d.address]}
                    onPending={setPending}
                    fav={favIds.includes(d.id)}
                    onFav={() => toggleFav(d.id)}
                  />
                ))}
              </div>
            </>
          )}
          {devices.length > 0 && (
            <>
              <h2 className="ah-ctl-section">Switches &amp; dimmers</h2>
              <div className="ah-ctl-grid">
                {devices.map((d) => (
                  <DeviceCard
                    key={d.id}
                    device={d}
                    email={user.email}
                    pending={pendingMap[d.address]}
                    onPending={setPending}
                    fav={favIds.includes(d.id)}
                    onFav={() => toggleFav(d.id)}
                  />
                ))}
              </div>
            </>
          )}
          {ble.length > 0 && (
            <>
              <h2 className="ah-ctl-section">Bluetooth lights</h2>
              {!bleOnline && (
                <p className="cs-help">
                  The house box isn&apos;t reporting (Home Assistant + BLE proxies —
                  see bridge/housebox/README.md). State may be stale and commands
                  will wait until it reconnects.
                </p>
              )}
              <div className="ah-ctl-grid">
                {ble.map((d) => (
                  <DeviceCard
                    key={d.id}
                    device={d}
                    email={user.email}
                    pending={pendingMap[d.address]}
                    onPending={setPending}
                    fav={favIds.includes(d.id)}
                    onFav={() => toggleFav(d.id)}
                  />
                ))}
              </div>
            </>
          )}
        </>
      )}

      {tab === 'climate' && (
        <>
          {docs !== null && !climate.length && !fans.length && (
            <p className="cs-help">No climate devices yet.</p>
          )}
          {climate.length > 0 && (
            <>
              <h2 className="ah-ctl-section">Climate</h2>
              {/* wide tracks — the temp row + mode chips need more than 185px */}
              <div className="ah-ctl-grid ah-aqua-thermo-grid">
                {climate.map((d) => (
                  <ClimateCard
                    key={d.id}
                    device={d}
                    email={user.email}
                    pending={pendingMap[d.address]}
                    onPending={setPending}
                  />
                ))}
              </div>
            </>
          )}
          {fans.length > 0 && (
            <>
              <h2 className="ah-ctl-section">Air</h2>
              <div className="ah-ctl-grid ah-aqua-thermo-grid">
                {fans.map((d) => (
                  <FanCard
                    key={d.id}
                    device={d}
                    email={user.email}
                    pending={pendingMap[d.address]}
                    onPending={setPending}
                  />
                ))}
              </div>
            </>
          )}
        </>
      )}

      {tab === 'shades' && (
        <>
          {docs !== null && !shades.length && (
            <p className="cs-help">No shades yet — they appear after the shade bridge&apos;s first sync.</p>
          )}
          {shades.length > 0 && (
            <>
              <h2 className="ah-ctl-section">Shades</h2>
              {!shadeOnline && (
                <p className="cs-help">
                  The shade bridge isn&apos;t reporting (bridge/shade_bridge.py on the
                  house LAN). Positions may be stale and commands will wait until it
                  reconnects.
                </p>
              )}
              <div className="ah-ctl-grid">
                {shades.map((d) => (
                  <ShadeCard
                    key={d.id}
                    device={d}
                    email={user.email}
                    pending={pendingMap[d.address]}
                    onPending={setPending}
                  />
                ))}
              </div>
            </>
          )}
        </>
      )}

      {tab === 'cameras' && (
        <>
          {docs !== null && !cameras.length && (
            <p className="cs-help">No cameras yet — they appear after the Ring mirror&apos;s first sync.</p>
          )}
          {cameras.length > 0 && (
            <>
              <h2 className="ah-ctl-section">Cameras</h2>
              {!ringOnline && (
                <p className="cs-help">
                  The camera mirror isn&apos;t reporting (bridge/ring_snap.py on the
                  house box) — thumbnails and motion below may be stale.
                </p>
              )}
              <div className="ah-cam-grid">
                {homeCams.map((d) => (
                  <CameraCard
                    key={d.id}
                    device={d}
                    alertOn={!!camAlerts?.enabled?.[d.id]}
                    onAlertToggle={() => toggleCamAlert(d.id)}
                  />
                ))}
              </div>
              {otherCams.length > 0 && (
                <>
                  <div className="ah-ctl-dead-head">
                    <h2 className="ah-ctl-section">
                      {otherCams[0].locationName || 'Other location'} (other house)
                    </h2>
                    <button
                      type="button"
                      className="cs-btn cs-btn-outline cs-btn-sm"
                      onClick={() => setShowOtherCams((v) => !v)}
                    >
                      {showOtherCams ? 'Hide' : `Show (${otherCams.length})`}
                    </button>
                  </div>
                  {showOtherCams && (
                    <div className="ah-cam-grid">
                      {otherCams.map((d) => (
                        <CameraCard
                          key={d.id}
                          device={d}
                          alertOn={!!camAlerts?.enabled?.[d.id]}
                          onAlertToggle={() => toggleCamAlert(d.id)}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}

      {/* Dead ISY nodes live on the Lights tab — it's all Insteon gear. */}
      {tab === 'lights' && unresponsive.length > 0 && (
            <>
              <div className="ah-ctl-dead-head">
                <h2 className="ah-ctl-section ah-ctl-section-dead">
                  Unresponsive ({unresponsive.length})
                </h2>
                <button
                  type="button"
                  className="cs-btn cs-btn-outline cs-btn-sm"
                  onClick={() => setShowUnresponsive(!showUnresponsive)}
                >
                  {showUnresponsive ? 'Hide' : 'Show'}
                </button>
              </div>
              {showUnresponsive ? (
                <div className="ah-ctl-grid">
                  {unresponsive.map((d) => (
                    <div key={d.id} className="ah-ctl-dead-card">
                      <DeviceCard
                        device={d}
                        email={user.email}
                        pending={pendingMap[d.address]}
                        onPending={setPending}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="cs-help" style={{ marginTop: 4 }}>
                  ISY nodes that aren&apos;t reporting status — likely unpowered, failed,
                  or removed hardware. Hidden from the map and device grid.
                </p>
              )}
            </>
      )}

      {tab === 'pool' && (
        <>
          {(() => {
            const aquaOnline =
              aquaBridge && Date.now() - (aquaBridge.heartbeat || 0) < BRIDGE_STALE_MS && aquaBridge.connected;
            const previewOnly = aqua.length > 0 && aqua.every((d) => d.demo);
            if (aquaOnline) {
              return (
                <p className="cs-help">
                  {`iAquaLink connected · ${aquaBridge.deviceCount || 0} pool devices · polls every minute`}
                </p>
              );
            }
            return (
              <p className="cs-help">
                {previewOnly
                  ? 'Preview data — toggles work locally so you can explore the layout. Connect the iAquaLink account on the bridge (bridge/README.md) and the real pool replaces this automatically.'
                  : 'iAquaLink not reporting — the pool component needs AQUALINK credentials on the bridge (bridge/README.md), and rides Zodiac\u2019s cloud so it needs internet.'}
              </p>
            );
          })()}
          {aqua.length === 0 && (
            <p className="cs-help">No pool devices yet — they appear after the bridge&apos;s first iAquaLink sync.</p>
          )}
          {aquaSystems.map((system) => (
            <AquaSystemSection
              key={system.name}
              system={system}
              email={user.email}
              pendingMap={pendingMap}
              onPending={setPending}
            />
          ))}
        </>
      )}

      {tab === 'media' && (
        <>
          {!bleOnline && (
            <p className="cs-help">
              The house box isn&apos;t reporting — media state may be stale and
              commands will wait until it reconnects.
            </p>
          )}
          {media.length === 0 && docs !== null && (
            <p className="cs-help">No media players yet — they appear after the house box&apos;s first sync.</p>
          )}
          {media.filter(isTvDevice).map((d) => (
            <MediaCard
              key={d.id}
              device={d}
              email={user.email}
              pending={pendingMap[d.address]}
              onPending={setPending}
              hero
            />
          ))}
          {media.some((d) => Array.isArray(d.groupMembers)) && (
            <>
              <h2 className="ah-ctl-section">House DJ</h2>
              {/* reachable only: the portable Move drops off when unplugged */}
              <DjPanel
                zones={media.filter((d) => Array.isArray(d.groupMembers) && d.reachable !== false)}
                email={user.email}
                deck={haBridge?.djSource ?? null}
                mic={haBridge?.djMic ?? null}
                vizDelayMs={Number(liveCfg?.djVizDelayMs) || 4000}
                hbFresh={bleOnline}
              />
            </>
          )}
          {media.some((d) => !isTvDevice(d)) && (
            <>
              <h2 className="ah-ctl-section">Music</h2>
              <div className="ah-ctl-grid ah-media-grid">
                {media.filter((d) => !isTvDevice(d)).map((d) => (
                  <MediaCard
                    key={d.id}
                    device={d}
                    email={user.email}
                    pending={pendingMap[d.address]}
                    onPending={setPending}
                  />
                ))}
              </div>
            </>
          )}
        </>
      )}

      {tab === 'programs' && (
        <>
          <AppAutomations
            config={liveCfg}
            nameOf={(id) => (docs || []).find((d) => d.id === id)?.name || id}
            snoozedUntil={snoozedUntil}
          />
          {programs === null && <p className="cs-help">Loading programs…</p>}
          {programs !== null && !programGroups.length && (
            <p className="cs-help">No programs yet — they appear after the bridge&apos;s next sync.</p>
          )}
          {programGroups.map(([path, list]) => (
            <div key={path}>
              <h2 className="ah-ctl-section">{path}</h2>
              <div className="ah-pgm-list">
                {list.map((p) => (
                  <ProgramRow
                    key={p.id}
                    program={p}
                    email={user.email}
                    pending={pendingMap[p.pgmId]}
                    onPending={setPending}
                  />
                ))}
              </div>
            </div>
          ))}
          {programGroups.length > 0 && (
            <p className="cs-help" style={{ marginTop: 16 }}>
              Programs are decoded from the ISY&apos;s D2D source. The green dot means the
              If condition is currently true. Enable/disable and manual runs take effect
              on the ISY immediately; logic edits still happen in the Admin Console for
              now (say the word and an in-app editor comes next).
            </p>
          )}
        </>
      )}

      {tab === 'network' && (
        <NetworkTab
          devices={netDevices}
          hbDocs={{
            _bridge: bridge,
            _aquaBridge: aquaBridge,
            _haBridge: haBridge,
            _shadeBridge: shadeBridge,
          }}
        />
      )}
    </div>
  );
}
